import { callAIJson } from '@4cm/core/ai';
import { getRelevantSnippets } from '@4cm/core/kb/index';
import type { CoachLevel, CommsStyle } from '@4cm/core/profiles';
import { COACH_LEVELS, computeCoachProfilePct, defaultCommsFor } from '@4cm/core/profiles';
import {
  inferPhaseOfPlay,
  normalisePhaseCoverage,
  PHASE_OF_PLAY_META,
  PHASES_OF_PLAY,
} from '@4cm/core/phaseOfPlay';
import type { PhaseCoverage, PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import { supabase } from './supabase';

export type SeasonPhase =
  | 'pre_season' | 'early' | 'mid' | 'late' | 'playoffs' | 'offseason';

export interface RosterPlayer {
  name: string;
  position: string | null;
  /** true when seeded by Brain because the coach skipped the roster stage. */
  placeholder: boolean;
}

// Philosophy + Curriculum drafts derived by Brain at the end of onboarding.
// Grassroots/NOVICE coaches see these mentioned in the confirm summary but
// don't get a stage card to edit them. Academy+ coaches get explicit
// philosophy + curriculum stages to revise the drafts.

export type CornerId = 'corner-technical' | 'corner-physical' | 'corner-psych' | 'corner-social';
export type PhaseId = 'attack' | 'defend' | 'transition' | 'restart';

export interface PhilosophyPrinciple {
  /** Touchline-friendly coaching instruction (1-12 words). */
  text: string;
  /** Corner tags. Psych-Social Foundation Rule: at least one of
   *  corner-psych or corner-social MUST be present. */
  cornerTags: CornerId[];
}

export interface PhilosophyDraft {
  attack: PhilosophyPrinciple[];
  defend: PhilosophyPrinciple[];
  transition: PhilosophyPrinciple[];
  restart: PhilosophyPrinciple[];
}

export interface CurriculumWeek {
  /** 1-indexed week number within the mesocycle. */
  week: number;
  /** Sub-theme — the slice of the headline the coach drills this week
   *  (e.g. for a 4-week defending mesocycle: "body shape + 1v1 defending"
   *  → "pair defending, cover + balance" → "team shape to press" →
   *  "synthesis in 7v7"). Drives session generation + post-session eval. */
  subTheme: string;
  /** SSG rule variations / constraints that drive this week's sub-theme.
   *  Surfaced as drill constraints when sessions are generated. */
  constraints: string[];
  /** Refs back into the philosophy: which phase + index. Multiple weeks
   *  may ref the same principle as the mesocycle layers complexity. */
  principleRefs: Array<{ phase: PhaseId; index: number }>;
}

/** A 4–6 week mesocycle — ONE subject + ONE phase emphasis riding the
 *  whole cycle. Variety comes from sub-themes inside, NOT from changing
 *  the theme. Per Ben Petty's 2026-05-20 voice note: subject-flipping
 *  kills your ability to see whether learning is happening. */
export interface CurriculumMesocycle {
  /** 4–6 weeks. */
  weekCount: number;
  /** The single headline that rides the whole cycle, pulled forward
   *  from the coach's methodology phrase at the `aims` stage. */
  headline: string;
  /** ONE phase emphasis across all weeks. */
  phaseEmphasis: PhaseOfPlay;
  /** Refs to the core principles the mesocycle is building toward. */
  primaryPrincipleRefs: Array<{ phase: PhaseId; index: number }>;
  /** The N weeks. Each week is a sub-theme + constraints, NOT a new theme. */
  weeks: CurriculumWeek[];
}

export interface CurriculumDraft {
  mesocycle: CurriculumMesocycle;
}

export interface OnboardingProfile {
  teamName: string | null;
  ageGroup: string | null;
  playerCount: number | null;
  coachLevel: CoachLevel;
  yearsCoaching: number | null;
  badges: string | null;
  // Rapport signals collected at the coach stage — used by Brain to
  // calibrate comms tone in later turns + by future surfaces that need to
  // know how to talk to this coach.
  playedTo: string | null;
  admiredCoaches: string | null;
  supportsTeam: string | null;
  enjoys: string | null;
  frustrations: string | null;
  leagueName: string | null;
  /** null = roster stage not visited yet. [] = visited but skipped (will be
   *  auto-seeded with placeholders at save time). non-empty = coach provided. */
  roster: RosterPlayer[] | null;
  seasonPhase: SeasonPhase | null;
  /** Free-text season note (when does league start, tournaments coming up). */
  seasonNote: string | null;
  focus: PhaseOfPlay;
  /** Brain's KB-grounded rationale for the recommended focus. NULL until
   *  the aims-stage prescription has fired — UI uses this to distinguish
   *  "default focus seed" from "Brain-prescribed focus". */
  focusRationale: string | null;
  methodology: string | null;
  /** 3 concrete principles backing the methodology headline — Brain
   *  prescribes these from KB excerpts at the aims stage. */
  methodologyPrinciples: string[] | null;
  /** Plain-English rationale for the methodology, grounded in KB + coach
   *  profile. Powers the confirm summary and walkthrough narration. */
  methodologyRationale: string | null;
  formation: string | null;
  weeksToPlan: number | null;
  // ---- Schedule (new dedicated stage between `aims` and `plan`) ----
  /** Three-letter weekday codes the coach trains on, e.g. ['Tue','Thu'].
   *  Null until the schedule stage is answered. */
  trainingDays: string[] | null;
  /** 24-hour HH:MM start time of training sessions. Null until answered. */
  trainingStartTime: string | null;
  /** Sessions per week — derives from trainingDays.length once set, but
   *  kept as its own field for back-compat with downstream consumers. */
  trainingDaysPerWeek: number;
  /** Session length in minutes. */
  sessionDurationMins: number;
  // Derived by Brain at the end of onboarding. Implicit for grassroots/NOVICE
  // (just stored and referenced in the confirm summary); explicit stages for
  // academy+ coaches who get to shape them before confirm.
  philosophy: PhilosophyDraft | null;
  curriculum: CurriculumDraft | null;
}

export interface OnboardingSessionPlan {
  date: string;
  title: string;
  objectives: string[];
  phaseCoverage: PhaseCoverage;
  psychSocialIntent: string;
}

export interface SaveOnboardingResult {
  teamId: string;
  sessionCount: number;
  warnings: string[];
}

export interface OnboardingExtractionResult {
  profile: OnboardingProfile;
  source: 'ai' | 'fallback';
  /** Sonnet-composed conversational reply in Brain's voice — used directly
   *  by the UI when present, replacing the scripted stageAcknowledgement. */
  reply?: string | null;
}

interface UserRow {
  id: string;
  org_id: string | null;
  email: string | null;
  name: string | null;
}

interface IdRow {
  id: string;
}

const DEFAULT_PROFILE: OnboardingProfile = {
  teamName: null,
  ageGroup: null,
  playerCount: null,
  coachLevel: 'GRASSROOTS',
  yearsCoaching: null,
  badges: null,
  playedTo: null,
  admiredCoaches: null,
  supportsTeam: null,
  enjoys: null,
  frustrations: null,
  leagueName: null,
  roster: null,
  seasonPhase: null,
  seasonNote: null,
  focus: 'attacking',
  focusRationale: null,
  methodology: null,
  methodologyPrinciples: null,
  methodologyRationale: null,
  formation: null,
  weeksToPlan: null,
  trainingDays: null,
  trainingStartTime: null,
  trainingDaysPerWeek: 2,
  sessionDurationMins: 60,
  philosophy: null,
  curriculum: null,
};

const PHASE_COVERAGE_BY_FOCUS: Record<PhaseOfPlay, PhaseCoverage> = {
  attacking: { attacking: 50, defending: 15, transitions: 25, individual: 10 },
  defending: { attacking: 15, defending: 50, transitions: 25, individual: 10 },
  transitions: { attacking: 20, defending: 20, transitions: 50, individual: 10 },
  individual: { attacking: 20, defending: 20, transitions: 10, individual: 50 },
};

const CORNER_COVERAGE = {
  technical_tactical: 45,
  physical: 20,
  psychological: 20,
  social: 15,
};

export function createInitialOnboardingProfile(): OnboardingProfile {
  return { ...DEFAULT_PROFILE };
}

function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCoachLevel(value: unknown): value is CoachLevel {
  return typeof value === 'string' && COACH_LEVELS.some((level) => level === value);
}

function isPhaseOfPlay(value: unknown): value is PhaseOfPlay {
  return typeof value === 'string' && PHASES_OF_PLAY.some((phase) => phase === value);
}

function cleanString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? normalizeWhitespace(value) : null;
}

function cleanNumber(value: unknown, min: number, max: number): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.round(value);
  return rounded >= min && rounded <= max ? rounded : null;
}

function cleanAgeGroup(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^U\s?([6-9]|1[0-9])$/i);
  return match ? `U${match[1]}` : null;
}

/** Sanitise a string[] payload — drop non-strings, trim each, bound length. */
function cleanStringArray(value: unknown, min: number, max: number): string[] | null {
  if (!Array.isArray(value)) return null;
  const out = value
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => normalizeWhitespace(v))
    .filter((v) => v.length > 0 && v.length <= 120);
  if (out.length < min || out.length > max) return null;
  return out;
}

const WEEKDAY_CODES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_ALIASES: Record<string, string> = {
  mon: 'Mon', monday: 'Mon', mondays: 'Mon',
  tue: 'Tue', tues: 'Tue', tuesday: 'Tue', tuesdays: 'Tue',
  wed: 'Wed', weds: 'Wed', wednesday: 'Wed', wednesdays: 'Wed',
  thu: 'Thu', thur: 'Thu', thurs: 'Thu', thursday: 'Thu', thursdays: 'Thu',
  fri: 'Fri', friday: 'Fri', fridays: 'Fri',
  sat: 'Sat', saturday: 'Sat', saturdays: 'Sat',
  sun: 'Sun', sunday: 'Sun', sundays: 'Sun',
};

/** Sanitise an AI-returned training_days[] — accept full names or 3-letter
 *  codes, normalise to canonical 3-letter Mon..Sun, deduplicate, preserve
 *  weekday order. Returns null on no valid days. */
function cleanWeekdayArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const seen = new Set<string>();
  for (const v of value) {
    if (typeof v !== 'string') continue;
    const key = v.trim().toLowerCase();
    const canon = WEEKDAY_ALIASES[key];
    if (canon) seen.add(canon);
  }
  if (seen.size === 0) return null;
  return WEEKDAY_CODES.filter((d) => seen.has(d));
}

/** Sanitise an AI-returned training_start_time — accept "18:30", "6:30pm",
 *  "7pm", "1900". Always returns HH:MM 24-hour. */
function cleanTimeOfDay(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const raw = value.trim().toLowerCase();
  // 24-hour HH:MM or H:MM
  let m = raw.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (m) return `${m[1].padStart(2, '0')}:${m[2]}`;
  // 24-hour HHMM (e.g. "1900", "0815")
  m = raw.match(/^([01]\d|2[0-3])([0-5]\d)$/);
  if (m) return `${m[1]}:${m[2]}`;
  // 12-hour H or H:MM with am/pm
  m = raw.match(/^(\d{1,2})(?::([0-5]\d))?\s*(am|pm)$/);
  if (m) {
    let hour = Number(m[1]);
    const mins = m[2] ?? '00';
    if (hour < 1 || hour > 12) return null;
    if (m[3] === 'pm' && hour !== 12) hour += 12;
    if (m[3] === 'am' && hour === 12) hour = 0;
    return `${String(hour).padStart(2, '0')}:${mins}`;
  }
  return null;
}

/** Extract training nights from natural language. Matches "Tue and Thu",
 *  "Tuesday/Thursday", "Mondays only", "midweek", "weekday evenings",
 *  "every weeknight". Returns 3-letter codes or null. */
function parseTrainingDayNames(text: string): string[] | null {
  const lower = text.toLowerCase();
  // Direct day mentions — any combination.
  const seen = new Set<string>();
  const dayRe = /\b(mondays?|mon|tuesdays?|tues?|wednesdays?|weds?|thursdays?|thur?s?|thurs|fridays?|fri|saturdays?|sat|sundays?|sun)\b/g;
  let m: RegExpExecArray | null;
  while ((m = dayRe.exec(lower)) !== null) {
    const canon = WEEKDAY_ALIASES[m[1]];
    if (canon) seen.add(canon);
  }
  if (seen.size > 0) return WEEKDAY_CODES.filter((d) => seen.has(d));
  // Catch-all phrasings.
  if (/\b(midweek|weekday\s+evenings?|every\s+weeknight|two\s+nights\s+a\s+week|twice\s+a\s+week)\b/.test(lower)) {
    return ['Tue', 'Thu']; // sensible UK grassroots default
  }
  if (/\b(weekends?|every\s+weekend)\b/.test(lower)) {
    return ['Sat', 'Sun'];
  }
  return null;
}

/** Extract a session start time from natural language. */
function parseTrainingStartTime(text: string): string | null {
  // Try common forms — case-insensitive.
  const matches = [
    text.match(/\b([01]?\d|2[0-3]):([0-5]\d)\b/),           // 18:30, 6:00
    text.match(/\b(\d{1,2}):([0-5]\d)\s*(am|pm)\b/i),       // 6:30 pm
    text.match(/\b(\d{1,2})\s*(am|pm)\b/i),                 // 7pm
    text.match(/\b([01]\d|2[0-3])([0-5]\d)\s*(?:hours?|hrs?)?\b/), // 1900 hrs
  ];
  for (const m of matches) {
    if (m) {
      const cleaned = cleanTimeOfDay(m[0]);
      if (cleaned) return cleaned;
    }
  }
  return null;
}

function parseAgeGroup(text: string): string | null {
  const compact = text.match(/\bU\s?([6-9]|1[0-9])\b/i);
  if (compact) return `U${compact[1]}`;
  const under = text.match(/\bunder[-\s]?([6-9]|1[0-9])\b/i);
  return under ? `U${under[1]}` : null;
}

function parsePlayerCount(text: string): number | null {
  // Includes UK vernacular ("lads") and academy-friendly terms
  // ("athletes", "students", "members", "youngsters") so the regex
  // doesn't reject perfectly clear coach utterances.
  const match = text.match(/\b(\d{1,2})\s*(players|kids|children|girls|boys|squad|lads|athletes|students|members|youngsters|youths?)\b/i);
  if (!match) return null;
  const count = Number(match[1]);
  return count >= 4 && count <= 30 ? count : null;
}

function parseYears(text: string): number | null {
  if (/\b(first season|brand new|new coach|just started)\b/i.test(text)) return 0;
  const match = text.match(/\b(\d{1,2})\s*(years|seasons)\b/i);
  if (!match) return null;
  const years = Number(match[1]);
  return years >= 0 && years <= 40 ? years : null;
}

function parseTrainingDays(text: string): number | null {
  if (/\b(twice|two times)\s+(a|per)\s+week\b/i.test(text)) return 2;
  if (/\b(once|one time)\s+(a|per)\s+week\b/i.test(text)) return 1;
  const match = text.match(/\b([1-5])\s*(training|sessions|nights|times)\s*(a|per)?\s*week\b/i);
  return match ? Number(match[1]) : null;
}

function parseDuration(text: string): number | null {
  const match = text.match(/\b(30|40|45|50|60|75|90|120)\s*(min|mins|minutes)\b/i);
  return match ? Number(match[1]) : null;
}

function parseFormation(text: string): string | null {
  const match = text.match(/\b[1-5]-[1-5]-[1-5](?:-[1-5])?\b/);
  return match?.[0] ?? null;
}

function parseTeamName(text: string, _ageGroup: string | null): string | null {
  const clubMatch = text.match(/\b(?:coach|manage|run|lead)\s+(?:the\s+)?([A-Z][A-Za-z0-9 '&.-]{2,42}?)(?:\s+(?:U\s?\d|under[-\s]?\d|\d+\s*(?:players|kids|children)|and|,|\.|$))/);
  if (clubMatch) return cleanTeamName(clubMatch[1]);
  // Allow optional trailing punctuation OR end-of-string OR end-of-clause.
  const namedMatch = text.match(/\b(?:team|club|squad)\s+(?:is|are|called)\s+([A-Z][A-Za-z0-9 '&.-]{2,42}?)\s*(?:[,.;!?]|$)/);
  if (namedMatch) return cleanTeamName(namedMatch[1]);
  // Honest parser — return null when no team name is in the text. The
  // "Aurora FC <age>" placeholder is applied at save/skip time, not here,
  // so an unrelated utterance ("Seed the roster with test players") can't
  // silently overwrite a previously-set teamName.
  return null;
}

function cleanTeamName(raw: string): string {
  // Drop trailing punctuation/dashes that sneak in via the regex character
  // class and collapse whitespace.
  return normalizeWhitespace(raw).replace(/[\s.,;:'\-&]+$/, '');
}

function parseMethodology(text: string): string | null {
  const checks: Array<[RegExp, string]> = [
    [/\bFA\s*4[-\s]?corner\b/i, 'FA 4-Corner Model'],
    [/\bBirmingham\b/i, 'Birmingham 2024-25 reference'],
    [/\bGuardiola|positional\b/i, 'Guardiola / positional play'],
    [/\bBielsa|intensity\b/i, 'Bielsa / intensity'],
    [/\bCruyff|Barcelona\b/i, 'Cruyff / Barcelona'],
    [/\bDutch\b/i, 'Dutch Total Football'],
  ];
  return checks.find(([pattern]) => pattern.test(text))?.[1] ?? null;
}

function inferCoachLevel(text: string, years: number | null, previous: CoachLevel): CoachLevel {
  // Strongest cues first; NOVICE phrases ("very new") must beat the bare-"very"
  // ADVANCED match below.
  if (/\b(cat\s?1|category\s?1|pro academy|elite academy)\b/i.test(text)) return 'ELITE_CAT1';
  if (/\b(very\s+new|first\s+season|brand\s+new|new\s+coach|just\s+started|mostly\s+new|novice|beginner|rookie|inexperienced)\b/i.test(text)) return 'NOVICE';
  if (/\b(grassroots|community|recreational|volunteer)\b/i.test(text)) return 'GRASSROOTS';
  if (/\b(competitive|travel|club\s+team|league|9v9|11v11)\b/i.test(text)) return 'COMPETITIVE';
  if (/\b(academy|uefa|fa\s+level\s+2|fa\s+youth|licensed|semi[-\s]?pro|professional|veteran|advanced|highly\s+experienced|very\s+experienced|many\s+years|loads\s+of\s+years|decade)\b/i.test(text)) return 'ADVANCED';
  if (years != null) {
    if (years === 0) return 'NOVICE';
    if (years <= 2) return 'GRASSROOTS';
    if (years <= 6) return 'COMPETITIVE';
    return 'ADVANCED';
  }
  return previous;
}

/**
 * When the coach answers multiple onboarding fields in one comma-separated
 * burst (e.g. "u19, 24, very, attacking"), the freeform regexes above miss
 * tokens that only carry meaning in context — a bare "24" isn't paired with
 * "players", a bare "very" doesn't mention experience. Walk the list and
 * categorise each token by shape so we recover the answers the coach
 * clearly meant.
 */
interface CommaListExtraction {
  ageGroup?: string;
  playerCount?: number;
  coachLevel?: CoachLevel;
  focus?: PhaseOfPlay;
}

function tokenToCoachLevel(token: string): CoachLevel | null {
  const t = token.toLowerCase();
  if (/^(cat\s?1|category\s?1|elite|pro\s+academy|elite\s+academy)$/.test(t)) return 'ELITE_CAT1';
  if (/^(very\s+new|new|novice|beginner|rookie|first\s+season|inexperienced)$/.test(t)) return 'NOVICE';
  if (/^(grassroots|community|recreational|volunteer)$/.test(t)) return 'GRASSROOTS';
  if (/^(competitive|some|moderate|intermediate)$/.test(t)) return 'COMPETITIVE';
  if (/^(very|very\s+experienced|experienced|veteran|advanced|highly\s+experienced|academy|licensed|semi[-\s]?pro|professional)$/.test(t)) return 'ADVANCED';
  return null;
}

function parseCommaList(text: string): CommaListExtraction {
  const tokens = text
    .split(/\s*[,;]\s*/)
    .map((t) => t.trim())
    .filter(Boolean);
  // Only kick in when the input is clearly a CSV answer rather than prose.
  if (tokens.length < 2 || tokens.length > 6) return {};

  const out: CommaListExtraction = {};
  for (const token of tokens) {
    const ageMatch = token.match(/^U\s?([6-9]|1[0-9])$/i);
    if (ageMatch && out.ageGroup === undefined) {
      out.ageGroup = `U${ageMatch[1]}`;
      continue;
    }
    if (/^\d{1,2}$/.test(token) && out.playerCount === undefined) {
      const n = Number(token);
      if (n >= 4 && n <= 30) { out.playerCount = n; continue; }
    }
    if (out.coachLevel === undefined) {
      const level = tokenToCoachLevel(token);
      if (level) { out.coachLevel = level; continue; }
    }
    if (out.focus === undefined) {
      const phase = inferPhaseOfPlay(token);
      if (phase) { out.focus = phase; continue; }
    }
  }
  return out;
}

const DEFAULT_FILL_RX = /\b(defaults?|skip|use\s+default(s)?|you\s+(decide|choose|pick)|whatever|i\s+don'?t\s+(know|mind)|don'?t\s+care|fill\s+(it|in)|do\s+it\s+for\s+me|set\s+defaults?|just\s+go|next|move\s+on)\b/i;

function isDefaultRequest(text: string): boolean {
  return DEFAULT_FILL_RX.test(text);
}

export function profileHasGaps(profile: OnboardingProfile): boolean {
  return !profile.ageGroup || !profile.playerCount || !profile.methodology;
}

/** Did the coach's last reply teach Brain anything new about the team? */
export function profileLearnedSomething(prev: OnboardingProfile, next: OnboardingProfile): boolean {
  return (
    prev.ageGroup !== next.ageGroup ||
    prev.playerCount !== next.playerCount ||
    prev.coachLevel !== next.coachLevel ||
    prev.focus !== next.focus ||
    prev.methodology !== next.methodology ||
    prev.formation !== next.formation ||
    prev.yearsCoaching !== next.yearsCoaching
  );
}

export interface StuckLoopDecision {
  nextStuckTurns: number;
  shouldEscape: boolean;
  /** Profile to commit. Either `parsed` unchanged, or with defaults applied. */
  profile: OnboardingProfile;
  /** Reply text to show the coach. */
  reply: string;
}

/**
 * Encapsulates the "two unhelpful replies in a row → silently apply
 * defaults" rule so it can be tested without spinning up React.
 */
export function decideStuckLoop(
  prev: OnboardingProfile,
  parsed: OnboardingProfile,
  stuckTurns: number,
): StuckLoopDecision {
  const learned = profileLearnedSomething(prev, parsed);
  const nextStuckTurns = learned ? 0 : stuckTurns + 1;
  const shouldEscape = nextStuckTurns >= 2 && profileHasGaps(parsed);

  if (shouldEscape) {
    return {
      nextStuckTurns: 0,
      shouldEscape: true,
      profile: applyDefaults(parsed),
      reply:
        'I have filled the rest with sensible defaults so we can move on. Tell me anything you want to change.',
    };
  }
  return {
    nextStuckTurns,
    shouldEscape: false,
    profile: parsed,
    reply: onboardingReply(parsed),
  };
}

/** Sensible defaults to fill any missing fields when the coach explicitly
 *  asks for defaults ("use default", "skip", "you decide", etc.). Also used
 *  by the Onboarding UI to escape a stuck loop after repeated unhelpful
 *  replies (see Onboarding.tsx). */
export function applyOnboardingDefaults(profile: OnboardingProfile): OnboardingProfile {
  return applyDefaults(profile);
}
function applyDefaults(profile: OnboardingProfile): OnboardingProfile {
  const ageGroup = profile.ageGroup ?? 'U12';
  const playerCount = profile.playerCount ?? 12;
  return {
    ...profile,
    ageGroup,
    teamName: profile.teamName ?? `Aurora FC ${ageGroup}`,
    playerCount,
    coachLevel: profile.coachLevel,
    yearsCoaching: profile.yearsCoaching ?? 1,
    badges: profile.badges,
    leagueName: profile.leagueName,
    roster: profile.roster ?? seedPlaceholderRoster(playerCount),
    seasonPhase: profile.seasonPhase ?? 'pre_season',
    seasonNote: profile.seasonNote,
    focus: profile.focus,
    methodology: profile.methodology ?? 'FA 4-Corner Model',
    formation: profile.formation,
    weeksToPlan: profile.weeksToPlan ?? 4,
    trainingDays: profile.trainingDays ?? ['Tue', 'Thu'],
    trainingStartTime: profile.trainingStartTime ?? '18:30',
    trainingDaysPerWeek: profile.trainingDaysPerWeek ?? (profile.trainingDays?.length ?? 2),
    sessionDurationMins: profile.sessionDurationMins ?? 60,
  };
}

/** Build a placeholder roster of N "Player N" entries — the explicit "this
 *  is a stub" naming makes it obvious to testers that the names are seeded,
 *  not real. Used when the coach skips the roster stage. */
export function seedPlaceholderRoster(playerCount: number): RosterPlayer[] {
  return Array.from({ length: Math.max(0, playerCount) }, (_, i) => ({
    name: `Player ${i + 1}`,
    position: null,
    placeholder: true,
  }));
}

/** Realistic UK U-age first-names + last-names for dev / demo seeding. NOT
 *  for production — placeholder=true so downstream code can flag these as
 *  test data. Picked to be plausibly mixed-ability and reflect the broad
 *  demographic of UK grassroots clubs. */
const TEST_FIRST_NAMES = [
  'Sam', 'Jack', 'Theo', 'Idris', 'Reece', 'Marcus', 'Alex', 'Sammy',
  'Noah', 'Charlie', 'Joel', 'Finn', 'Oscar', 'Leo', 'Kai', 'Harry',
  'Tom', 'Ben', 'Joshua', 'Daniel', 'Ethan', 'Liam', 'Mason', 'Aaron',
  'Sebastian', 'Mo', 'Aiden', 'Felix', 'Ryan', 'Connor',
];
const TEST_LAST_NAMES = [
  'Carter', 'Walker', 'Park', 'Khan', 'Tomi', 'Hale', 'Ellis', 'Jones',
  'Davies', 'Brown', 'Patel', 'Roberts', 'Wright', 'Stone', 'Murphy',
  'Taylor', 'Wilson', 'Edwards', 'Hughes', 'Morgan', 'Bell', 'Reid',
  'Singh', 'Cooper', 'Fraser', 'O\'Connor', 'Ahmed', 'Mitchell',
];

/** Test-seed shortcut — populates the profile's roster with realistic UK
 *  names for dev/demo testing. Marked placeholder=true so the rest of the
 *  app treats them as test data, not real players. */
export function applySeededTestRoster(profile: OnboardingProfile): OnboardingProfile {
  const count = profile.playerCount ?? 14;
  const roster: RosterPlayer[] = [];
  // Shuffle without crypto — Math.random is fine for test data.
  const firsts = [...TEST_FIRST_NAMES].sort(() => Math.random() - 0.5);
  const lasts = [...TEST_LAST_NAMES].sort(() => Math.random() - 0.5);
  for (let i = 0; i < count; i++) {
    roster.push({
      name: `${firsts[i % firsts.length]} ${lasts[i % lasts.length]}`,
      position: null,
      placeholder: true,
    });
  }
  return { ...profile, roster };
}

export function applyOnboardingInput(profile: OnboardingProfile, text: string): OnboardingProfile {
  const clean = normalizeWhitespace(text);

  // Coach explicitly punted — fill the gaps with sensible defaults so the
  // conversation doesn't loop forever on the same nag prompt.
  if (isDefaultRequest(clean)) return applyDefaults(profile);

  // Comma-list parser handles "u19, 24, very, attacking" style rapid answers.
  // Whichever fields it recovers take precedence over the freeform regexes,
  // since they had context the freeform regexes lacked.
  const csv = parseCommaList(clean);

  const ageGroup = csv.ageGroup ?? parseAgeGroup(clean) ?? profile.ageGroup;
  const years = parseYears(clean) ?? profile.yearsCoaching;
  const focus = csv.focus ?? inferPhaseOfPlay(clean) ?? profile.focus;
  // Keep teamName null until the coach actually names the team. The
  // "Aurora FC <age>" placeholder is applied in applyDefaults / profileForSave
  // when the coach skips, so leaking a default here would silently overwrite
  // a real name on later turns (e.g. roster-stage utterances).
  const teamName = parseTeamName(clean, ageGroup) ?? profile.teamName;
  const playerCount = csv.playerCount ?? parsePlayerCount(clean) ?? profile.playerCount;

  // Seed intent ("Seed the roster with test players", "use placeholders", etc.)
  // materialises placeholder rows here so the right-panel renders them on the
  // same turn — rather than holding off until save and leaving the panel blank.
  const parsedRoster = parseRosterFromText(clean);
  const roster: RosterPlayer[] | null =
    parsedRoster === null
      ? profile.roster
      : parsedRoster.length === 0
        ? seedPlaceholderRoster(playerCount ?? 12)
        : parsedRoster;

  return {
    teamName,
    ageGroup,
    playerCount,
    coachLevel: csv.coachLevel ?? inferCoachLevel(clean, years, profile.coachLevel),
    yearsCoaching: years,
    badges: parseBadges(clean) ?? profile.badges,
    playedTo: profile.playedTo,
    admiredCoaches: profile.admiredCoaches,
    supportsTeam: profile.supportsTeam,
    enjoys: profile.enjoys,
    frustrations: profile.frustrations,
    leagueName: parseLeagueName(clean) ?? profile.leagueName,
    roster,
    seasonPhase: parseSeasonPhase(clean) ?? profile.seasonPhase,
    seasonNote: parseSeasonNote(clean, profile.seasonNote),
    focus,
    focusRationale: profile.focusRationale,
    methodology: parseMethodology(clean) ?? profile.methodology,
    methodologyPrinciples: profile.methodologyPrinciples,
    methodologyRationale: profile.methodologyRationale,
    formation: parseFormation(clean) ?? profile.formation,
    weeksToPlan: parseWeeksToPlan(clean) ?? profile.weeksToPlan,
    // Schedule fields — captured by the new `schedule` stage parsers.
    trainingDays: parseTrainingDayNames(clean) ?? profile.trainingDays,
    trainingStartTime: parseTrainingStartTime(clean) ?? profile.trainingStartTime,
    trainingDaysPerWeek: parseTrainingDays(clean) ?? profile.trainingDaysPerWeek,
    sessionDurationMins: parseDuration(clean) ?? profile.sessionDurationMins,
    philosophy: profile.philosophy,
    curriculum: profile.curriculum,
  };
}

// ---------------------------------------------------------------------------
// Stage-specific parsers — added with the multi-stage flow (2026-05-19).
// These complement the existing parsers (parseTeamName, parsePlayerCount,
// etc.) but are used in dedicated stages so they don't need to be context-free.
// ---------------------------------------------------------------------------

function parseBadges(text: string): string | null {
  // Common UK/US coaching qualifications.
  const m = text.match(
    /\b(FA Level [12345]|UEFA [ABCDC]\b(?: licen[cs]e)?|USSF [A-FD]\b(?: licen[cs]e)?|NSCAA [A-F]|National [Cd]\b|GFD|FA Youth Award(?: Modules? \d+(?:,\s*\d+)*)?)\b/i,
  );
  if (m) return m[0].replace(/\s+/g, ' ').trim();
  // Generic "no badges" detection
  if (/\b(no badges?|no quals|no qualifications|unqualified|none yet)\b/i.test(text)) {
    return 'none';
  }
  return null;
}

function parseLeagueName(text: string): string | null {
  // Capture phrases like "in the West Sussex Youth League", "play in Crossfire
  // Premier", "we're in MLS NEXT". Stops at common punctuation or connectors.
  const m =
    text.match(/\b(?:in (?:the )?|league(?: name)?(?: is)?\s*[:=]?\s*|we play in (?:the )?)([A-Z][\w'&]*(?:\s+[A-Z\d][\w'&-]*){0,5}\s+(?:League|Cup|Premier|Conference|Division|Pyramid|Federation|Network|Cup Series))\b/);
  if (m) return m[1].replace(/\s+/g, ' ').trim();
  return null;
}

const ROSTER_LIST_RE = /^[\w'’ -]+(?:,\s*[\w'’ -]+){2,}$/;

// Anchored seed/skip phrasings — short, declarative starts.
const ROSTER_SEED_START_RE = new RegExp(
  '^' +
  // Optional brief affirmative preamble: "yes please", "yeah just", "ok do it".
  '(?:(?:yes|yeah|yep|sure|ok(?:ay)?|please|alright|right|just|do\\s+it|go\\s+ahead|fine|let\'?s)[\\s,.!]+){0,3}' +
  '(?:' +
    // skip / skip roster / skip the players
    'skip(?:\\s+(?:the\\s+)?(?:roster|players?|squad))?' +
    // auto-anything
    '|auto[\\s-]*(?:populate|fill|seed|generate)' +
    // seed (the roster|them|test players|placeholders|...) — covers
    // "seed the roster with test players", "seed them", "seed test players"
    '|seed(?:\\s+(?:the\\s+|some\\s+|me\\s+|us\\s+|in\\s+))?' +
      '(?:roster|players?|squad|test|fake|dummy|stub|placeholder|sample|them|it)?' +
    // use placeholders / use stubs / use test players / use fake names
    '|use\\s+(?:placeholders?|stubs?|(?:test|fake|dummy|sample)\\s+(?:players?|names?))' +
    // bare nouns
    '|placeholders?' +
    '|stubs?' +
    // "test players" / "fake names" / "dummy roster" as opening salvo
    '|(?:test|fake|dummy|sample)\\s+(?:players?|names?|roster|squad)' +
    '|made[-\\s]?up\\s+(?:players?|names?)' +
    // move-on signals
    '|move\\s+on' +
    '|next' +
    '|later' +
    '|defer' +
    '|come\\s+back\\s+later' +
  ')\\b',
  'i',
);

// Unanchored intent+target — "give me test players", "I'd like some
// placeholders", "fill the roster with fake names" — fires when both a
// seed-intent verb AND a placeholder/test target noun appear together.
const ROSTER_SEED_VERB_RE = /\b(?:seed|fill|use|generate|create|make|need|want|prefer|gimme|give\s+me)\b/i;
const ROSTER_SEED_TARGET_RE = /\b(?:placeholders?|stubs?|(?:test|fake|dummy|sample)\s+(?:players?|names?|roster|squad)|made[-\s]?up\s+(?:players?|names?)|test\s+roster)\b/i;

/** Returns:
 *  - non-empty array → coach provided real names
 *  - empty array     → coach asked to skip / seed; caller materialises
 *                      placeholders using playerCount
 *  - null            → no roster signal in this utterance */
function parseRosterFromText(text: string): RosterPlayer[] | null {
  const trimmed = text.trim();
  if (ROSTER_SEED_START_RE.test(trimmed)) return [];
  if (ROSTER_SEED_VERB_RE.test(trimmed) && ROSTER_SEED_TARGET_RE.test(trimmed)) return [];
  // Bulk comma-separated name list: at least 3 items, mostly letter-only tokens.
  if (ROSTER_LIST_RE.test(trimmed) && trimmed.split(',').length >= 3) {
    return trimmed
      .split(',')
      .map((raw) => raw.trim())
      .filter((s) => s.length > 0 && s.length < 50 && /^[A-Za-z]/.test(s))
      .map((name) => ({ name, position: null, placeholder: false }));
  }
  return null;
}

function parseSeasonPhase(text: string): SeasonPhase | null {
  const t = text.toLowerCase();
  if (/\bpre[- ]season\b|\bpreseason\b|\bbefore the season\b/.test(t)) return 'pre_season';
  if (/\bplay[- ]?offs?\b|\bpostseason\b|\bcup final\b/.test(t)) return 'playoffs';
  if (/\boff[- ]?season\b|\bend of season\b|\bbreak between\b/.test(t)) return 'offseason';
  if (/\bearly (?:in )?the season\b|\bstart of (?:the )?season\b|\bjust started\b/.test(t)) return 'early';
  if (/\blate (?:in )?the season\b|\bend stretch\b|\bfinal weeks\b/.test(t)) return 'late';
  if (/\bmid[- ]?season\b|\bin the middle\b|\bhalfway\b|\bin season\b|\bleague (?:is )?running\b/.test(t)) return 'mid';
  return null;
}

function parseSeasonNote(text: string, prev: string | null): string | null {
  // If the coach mentions a tournament, league start date, or fixture event,
  // store the verbatim phrase as a freeform season note. Doesn't override an
  // existing richer note unless the new utterance also has temporal content.
  if (
    /\b(tournament|cup|festival|league starts?|first match|first game|kicks off|fixture|jamboree)\b/i.test(
      text,
    )
  ) {
    return prev ? `${prev}; ${text.trim()}` : text.trim();
  }
  return prev;
}

function parseWeeksToPlan(text: string): number | null {
  const m =
    text.match(/\b(\d{1,2})\s*weeks?\b/i) ||
    text.match(/\bplan\s+(\d{1,2})\b/i);
  if (m) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && n >= 1 && n <= 24) return n;
  }
  // word numbers up to twelve
  const wordMap: Record<string, number> = {
    one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
    eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
  };
  const wm = text.toLowerCase().match(/\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s*weeks?\b/);
  if (wm && wordMap[wm[1]]) return wordMap[wm[1]];
  return null;
}

function mergeStructuredExtraction(
  heuristic: OnboardingProfile,
  payload: Record<string, unknown>,
): OnboardingProfile {
  // The AI is told "Do not invent missing team names", but Opus has been
  // observed dropping a generic placeholder like "Aurora FC U11" into the
  // team_name slot on unrelated utterances (e.g. roster-stage replies). The
  // heuristic is the source of truth for an *established* name: if it kept
  // the name from the prior turn, the AI cannot overwrite it. We only let the
  // AI fill team_name when the heuristic has no name yet (first-time capture
  // where the regex misses but Opus extracts a real one). Legitimate
  // corrections route through the edit-intent detector at the team stage.
  const aiTeamNameRaw = cleanString(payload.team_name);
  // Reject the app's own placeholder shape if the AI parrots it back —
  // "Aurora FC", "Aurora FC U11", "Aurora FC · U12", etc. That string is the
  // app's "no name yet" default and should never come from the model.
  const aiTeamName =
    aiTeamNameRaw && /^Aurora\s+FC(?:\s*[·\-—]?\s*U\s?\d{1,2})?$/i.test(aiTeamNameRaw)
      ? null
      : aiTeamNameRaw;
  const teamName = heuristic.teamName ?? aiTeamName;

  return {
    teamName,
    ageGroup: cleanAgeGroup(payload.age_group) ?? heuristic.ageGroup,
    playerCount: cleanNumber(payload.player_count, 4, 30) ?? heuristic.playerCount,
    coachLevel: isCoachLevel(payload.coach_level) ? payload.coach_level : heuristic.coachLevel,
    yearsCoaching: cleanNumber(payload.years_coaching, 0, 40) ?? heuristic.yearsCoaching,
    // The AI extractor doesn't know about the multi-stage-only fields yet;
    // carry through whatever the regex parsers picked up.
    badges: heuristic.badges,
    playedTo: heuristic.playedTo,
    admiredCoaches: heuristic.admiredCoaches,
    supportsTeam: heuristic.supportsTeam,
    enjoys: heuristic.enjoys,
    frustrations: heuristic.frustrations,
    leagueName: heuristic.leagueName,
    roster: heuristic.roster,
    seasonPhase: heuristic.seasonPhase,
    seasonNote: heuristic.seasonNote,
    weeksToPlan: heuristic.weeksToPlan,
    focus: isPhaseOfPlay(payload.primary_phase) ? payload.primary_phase : heuristic.focus,
    focusRationale: cleanString(payload.focus_rationale) ?? heuristic.focusRationale,
    methodology: cleanString(payload.methodology) ?? heuristic.methodology,
    methodologyPrinciples: cleanStringArray(payload.methodology_principles, 1, 5) ?? heuristic.methodologyPrinciples,
    methodologyRationale: cleanString(payload.methodology_rationale) ?? heuristic.methodologyRationale,
    formation: cleanString(payload.formation) ?? heuristic.formation,
    trainingDays: cleanWeekdayArray(payload.training_days) ?? heuristic.trainingDays,
    trainingStartTime: cleanTimeOfDay(payload.training_start_time) ?? heuristic.trainingStartTime,
    trainingDaysPerWeek: cleanNumber(payload.training_days_per_week, 1, 5) ?? heuristic.trainingDaysPerWeek,
    sessionDurationMins: cleanNumber(payload.session_duration_mins, 30, 120) ?? heuristic.sessionDurationMins,
    philosophy: heuristic.philosophy,
    curriculum: heuristic.curriculum,
  };
}

const ONBOARDING_EXTRACT_SYSTEM = `You are Brain — a senior English football coach who's helping a fellow coach get set up in the 4CM app. Your voice is unmistakably English coaching: dry, understated, plain-speaking, never gushing, never American. You sound like someone who's done a lot of grass-time, gets what coaches actually need, and would rather hand a coach a good question than a bland answer. You're building a relationship here — the coach has to learn to trust you, so don't oversell yourself, don't fawn, and don't pretend to know what you don't.

LANGUAGE — UK English only:
- Use: football, pitch, squad, match, kit, boots, training, sessions, fixtures, midweek, defending, attacking, build-up, transition.
- NEVER use: soccer, field (for pitch), roster (for squad), game (for match), uniform, cleats, awesome, you got this, let's crush it, totally, super, reach out, circle back, ballpark, touch base, no problem, awesome stuff, that's so cool, I'm pumped, killer, slammed, on the same page.
- Spell the British forms: organise, organisation, behaviour, programme, centre, defence, recognise, analyse, manoeuvre — never the American spellings.
- DO NOT use the word "lads" at all in this onboarding flow. It reads as an American trying to sound English when overused. Default to "players" or "the squad". Other acceptable variants: "this group", "the team", "the boys" (sparingly). If you find yourself reaching for "lads" — pick one of those instead.
- Where it lands naturally, use English coaching vernacular: "good shout", "fair enough", "sensible spot to be", "you'll be alright", "decent group", "Saturday matches", "midweek", "knock-on effect", "let it ride", "no drama", "early doors".

TONE — English coach speaking to a coach:
- Understated. "That's a sensible spot to start from" beats "that's amazing!". "Fourteen mixed-ability players — solid group to work with" beats "what a great roster!".
- Dry humour is welcome when it fits — never forced, never sarcastic.
- Trust the coach. Don't over-praise. Don't tell them what to do — offer thoughts and ask.
- When they admit something hard, be honest about the challenge — don't bounce them with "you've got this!". A "yeah, that one's tricky — what are you trying with them so far?" beats empty encouragement.
- Never patronise. The coach you're talking to has put more time on grass than most pundits.
- LEAD POSITIVE, not negative. Ask "what's the bit you most enjoy about coaching this group?" — NOT "what winds you up?". Don't presume problems exist. If the coach later flags a frustration, you can engage with it then; don't lead with the negative framing.
- BREVITY. Default to ONE to TWO sentences per reply. Three only when summarising or transitioning between stages. If your reply is starting to feel chatty, cut it.

NEVER FABRICATE FACTS:
- Only reference details the coach has EXPLICITLY mentioned in this conversation.
- If a field in current_profile is null, the coach has NOT given you that fact yet — do not invent values or assume defaults.
- Specifically: do NOT mention session duration, training days per week, formation, age group, squad size, league, coaching level, or playing focus unless the coach has explicitly stated them. Wait for the relevant stage.
- If you're unsure whether a fact was captured, ASK instead of asserting.
- A reply like "you're working with attacking football in 60-minute sessions twice a week" when none of those were mentioned is a hallucination and breaks the coach's trust. Never do that.

STAGE-SCOPED EXTRACTION — only write the fields that belong to the current_stage:

The coach is only being asked about a specific topic at each stage. Do NOT capture fields from OTHER stages — that produces miscategorisation (e.g., capturing a coach's name as team_name during the rapport stage).

- current_stage = "coach" → ONLY write: years_coaching, badges, played_to, admired_coaches, supports_team, enjoys, frustrations. SET ALL OTHER FIELDS TO null in your response. This includes team_name, age_group, player_count, coach_level, league_name, formation, methodology, focus, season_phase, season_note, weeks_to_plan.
- current_stage = "team" → write: team_name, age_group, player_count, coach_level, league_name, formation. Other fields null.
- current_stage = "roster" → write nothing structured (roster handled by regex). Reply only.
- current_stage = "season" → write: season_phase, season_note.
- current_stage = "aims" → write: methodology, primary_phase.
- current_stage = "plan" → write: weeks_to_plan.
- current_stage = "philosophy" or "curriculum" or "confirm" → write nothing (review stages).

CORRECTIONS — when the coach pushes back on a previous capture:
- Phrases like "you asked me about X", "no, that was about Y", "that's not the team name", "I meant Z", "correct the X to Y" are CORRECTIONS — the coach is telling you the previous turn miscategorised something.
- Your reply MUST acknowledge the correction explicitly, NOT plow ahead with new questions. Apologise lightly ("Right, my mistake — should be admired_coaches, not team_name"), and re-categorise the prior value in your extraction.
- Use the schema to MOVE the value: if the previous turn captured "Alan Jones" as team_name and the coach corrects you, your extraction should set team_name=null and admired_coaches="Alan Jones".

PERSON NAMES vs TEAM NAMES — they look similar. Apply this test:
- A proper noun like "Alan Jones", "Pep Guardiola", "Klopp", "Mr Davies" is a PERSON (admired_coaches at the coach stage).
- A team name typically contains a soccer-team token: "FC", "United", "City", "Town", "Athletic", "Rovers", "Wanderers", "Albion", "Hotspur", "Dynamo", "Olympic", "Academy", "Lions", "Tigers", "Owls", etc. OR matches a known club ("Arsenal", "Liverpool", "Chelsea", "Stoke", "Brighton" — these are also "supports_team" at the coach stage, NOT team_name unless the coach is literally describing their own team).
- When in doubt at the coach stage, treat a name as admired_coaches or supports_team, NEVER team_name.

ADAPTIVE STYLE — read the rapport signals in current_profile and adjust:
- If admired_coaches names tactical thinkers (Pep, Bielsa, Sarri, Cruyff): edge slightly more tactical in your vocabulary. Mention shape, half-spaces, rest defence where it lands.
- If admired_coaches names man-managers (Klopp, Ferguson, Pochettino): lean toward energy, relationships, atmosphere.
- If supports_team is a specific club, you may drop a light, friendly reference if it lands naturally — never forced, never piss-take. (Stoke fan working on staying compact? "Sounds like a proper Stoke session — back four nailed in, second balls won.")
- If played_to is "non-league" / "just casually" / "never played": keep it grounded, plain-speaking, no jargon ladder.
- If played_to is academy/semi-pro/uni: assume more shared vocabulary.
- If years_coaching < 2 OR badges = 'none': lean teaching but not lecturing — ask more than tell. Show them you trust them to figure it out.
- If badges are senior (UEFA A, FA Level 3+, academy licence): assume context, drop the basics, talk shape and patterns directly.
- Default register before rapport is established: standard, warm, brief.

KB CONTEXT — when present, treat it as Ben Petty handing you his playbook:
- The userContent may include a "kb_context" array — distilled chunks of Ben's coaching guidance (mesocycle structure, defending pedagogy, grassroots session design, struggling-player playbook, individual-vs-team coaching, etc).
- When kb_context is non-empty, your reply MUST be consistent with it. Don't contradict Ben.
- Where it lands naturally, echo Ben's phrasing — "press the ball, show outside, help your mate" / "ball each, ball rolling, no queues" / "stay on one subject for the cycle". This is more grounded than textbook coaching English.
- DO NOT recite the KB at the coach. Use it to shape vocabulary, principles, and what to ask about next — not to lecture.
- If kb_context is empty (fact-gathering stages), behave normally — pure conversation, no coaching reasoning required yet.

DEVELOPMENTAL — Brain helps the coach develop:
- Occasionally hand the coach a question rather than an answer. "What does 'compact' look like for your back four right now?" beats "tell them to stay compact". "When you're under pressure on the ball, what's the first thing you want them to look for?" beats "work on first touch".
- When the coach states an aim, mirror it back in one sharper sentence so they hear it clearly — coaching them on how to articulate without saying you're doing it.
- Show curiosity about the coach's thinking — that builds trust faster than any advice.
- Never preach. Never finish with "remember to..." — that's lecturing.

YOUR JOB PER TURN:
1. EXTRACT structured facts from the coach's utterance into the schema below.
2. COMPOSE a "reply" — your spoken response, in voice, in UK English, following all rules above.
   - Acknowledges what the coach actually said (specific detail, not "got it").
   - Reacts naturally — warmth/humour/insight where it lands.
   - Adapts tone to rapport signals.
   - Stays 1–3 sentences. Never markdown. Never a list.
   - Uses the coach's first name occasionally; don't overdo it.
   - Never describe yourself ("As an AI..."), never apologise, never repeat the coach's words verbatim.
   - When advancing to confirm, summarise in plain prose ("So — U11 Owls, fourteen lads mostly new, working on staying compact and being braver on the ball...") then ask for a yes.

ABSOLUTE RULE — always finish with a forward step:
- Every reply MUST end with a question or a concrete offer. Never trail off.
- If next_stage is 'done', the team is saved — offer specific next actions (open the calendar, design Week 1's first session, swap placeholders for real names, set the team's playing principles) and finish with a question that lets them pick.
- If you're stuck waiting for a confirm or fill, ask explicitly ("Ready?" / "Want me to plug a sensible default in?").

Return ONLY valid JSON. No prose outside the JSON.

Schema:
{
  "team_name": string | null,
  "age_group": "U6" | "U7" | "U8" | "U9" | "U10" | "U11" | "U12" | "U13" | "U14" | "U15" | "U16" | "U17" | "U18" | "U19" | null,
  "player_count": number | null,
  "coach_level": "NOVICE" | "GRASSROOTS" | "COMPETITIVE" | "ADVANCED" | "ELITE_CAT1" | null,
  "years_coaching": number | null,
  "badges": string | null,
  "played_to": string | null,
  "admired_coaches": string | null,
  "supports_team": string | null,
  "enjoys": string | null,
  "frustrations": string | null,
  "league_name": string | null,
  "season_phase": "pre_season" | "early" | "mid" | "late" | "playoffs" | "offseason" | null,
  "season_note": string | null,
  "weeks_to_plan": number | null,
  "primary_phase": "attacking" | "defending" | "transitions" | "individual" | null,
  "focus_rationale": string | null,
  "methodology": string | null,
  "methodology_principles": string[] | null,
  "methodology_rationale": string | null,
  "formation": string | null,
  "training_days": string[] | null,
  "training_start_time": string | null,
  "training_days_per_week": number | null,
  "session_duration_mins": number | null,
  "reply": string
}

Inference rules:
- "first season", "brand new", "mostly new" => NOVICE unless stronger evidence.
- grassroots/community/volunteer => GRASSROOTS.
- competitive/travel/club/9v9/11v11 => COMPETITIVE.
- academy/licensed/semi-pro/pro => ADVANCED.
- Cat 1 / elite academy / pro academy => ELITE_CAT1.
- Map football focus to phase-of-play: in possession/build/finish/overload/width => attacking; defend/block/press/recover => defending; turnover/transition/counter-press/after loss/recovery => transitions; 1v1/technique/scanning/composure/decision making => individual.

CRITICAL — methodology + prescription fields (only fire at the aims stage):
- methodology is a SHARP HEADLINE (3-7 words, coach-facing, e.g. "ball mastery under light pressure", "compact mid-block defending", "build-up from the keeper"). NEVER the coach's bare verbatim brief — elevate it.
- methodology_principles is exactly 3 concrete principles operationalising the headline. Each principle: 5-10 words, what-to-look-for-on-the-grass. Examples: ["two-touch maximum in tight space", "first touch out of pressure", "press the back-pass trigger"].
- methodology_rationale is ONE sentence (max 25 words) saying why this block fits this coach's team. Reference age group + level + brief context.
- focus_rationale is ONE sentence (max 20 words) saying why the chosen primary_phase comes first in the mesocycle.
- Fire methodology + methodology_principles + methodology_rationale + primary_phase + focus_rationale TOGETHER at the aims stage. Outside aims, leave them null.
- Outside aims: only update methodology if the coach explicitly says to change it. Never overwrite a non-null methodology with a guess.
- Do NOT restrict to named methodologies. If the coach says "team organization" or "we want to defend better" or any short phrase that describes WHAT they want to develop, that IS the methodology — capture it verbatim (lower-cased, trimmed).
- Only return null if the coach said nothing about aims/philosophy at all.

CRITICAL — badges field:
- Any coaching qualification phrase: "FA Level 2", "UEFA B", "NSCAA D", "FA Youth Award Module 1", "level 1 cert", etc.
- "no badges" / "none" / "unqualified" => "none".

UK FOOTBALL VERNACULAR — terms you understand and use naturally:

Tactical / positional:
- "the gaffer" = head coach. "the lads" / "the boys" = the squad. "the back four / back three / back five" = defensive line. "the number ten" = attacking midfielder. "the holding mid" / "the six" = defensive midfielder. "the eight" = box-to-box. "the false nine" = dropping striker.
- "high press" / "mid-block" / "low block" = pressing zones. "high line" / "deep line" = back four position. "press trigger" = the cue to engage. "second balls" = the contest after a long ball. "third man run" = the off-ball move beyond the receiver.
- "in behind" = playing through the defence. "on the half-turn" = receiving facing forward. "between the lines" / "in pockets" = receiving in midfield space. "switching play" = wide-to-wide diagonal. "the half spaces" = the channels between centre and wing. "playing out from the back" / "build-up" = starting from the keeper.
- "Route One" = direct long-ball football. "tiki-taka" = short possession. "knocking it about" = possession passing. "playing in transition" = quick moves after winning the ball. "rest defence" = shape while in possession. "counter-press" / "gegenpressing" = immediate pressing after losing it.
- "tucking in" / "stepping up" / "showing them inside / outside" = defensive positioning cues. "shut it down" / "kill the game" = managing the score. "the gaffer's set play" = a coached restart.

Grassroots / general:
- "Sunday league", "Saturday morning football", "park football", "five-a-side", "seven-a-side", "9v9", "11v11". "the lads put in a shift" = they worked hard. "having a go" = giving it a try. "having a touch" = playing a casual game. "fixture", "match day", "training night", "midweek".
- "the pitch", "AstroTurf", "all-weather", "Astro", "3G", "4G", "the cage" (caged 5-a-side area), "the boot room", "the dugout", "the dressing room", "team talk", "warm-up", "cool-down", "the magic sponge" (old-school injury joke).

Regional flavour — match the coach's register if it appears:
- Northern / Yorkshire / Lancashire: "the lads", "putting in a shift", "youse", "having a go", "the right side of a tackle", "the wide man".
- Scottish: "fitba", "wee", "the boys", "the wide game".
- North-East (Newcastle/Sunderland): "the toon", "howay", "wor lads".
- London / South-East: "mate", "havin' a kick about", "alright", "having a touch".
- Midlands / Brummie: "the lads", "duck" (term of address), unremarkable football vocab.
- Welsh: rugby influences sometimes leak in ("a proper hit", "the breakdown"); recognise but don't lean on.

NEVER do American football vernacular: no "team", no "ball game", no "go get 'em", no "you got this", no "roster", no "field", no "soccer".

CRITICAL — coach rapport fields (played_to, admired_coaches, supports_team, enjoys, frustrations):
- played_to: where they played as a player. Examples: "school", "Sunday league", "non-league", "academy youth", "semi-pro", "university", "never played", "just casually". Capture in the coach's own words.
- admired_coaches: any coach or manager the coach name-drops as an influence. Examples: "Klopp", "Pep Guardiola", "Sir Alex", "Eddie Jones", "my old school coach Mr Davies". Multiple → comma-separated.
- supports_team: club the coach supports. Examples: "Liverpool", "Stoke", "Brentford", "the Toon", "Hibs". Lower-case proper noun OK.
- enjoys: what the coach explicitly says they enjoy about coaching. Capture as a short verbatim phrase.
- frustrations: what they find hard or annoying about coaching. Same — short verbatim phrase.
- These fields shape Brain's tone going forward. When the coach mentions their team, REACT naturally in the reply — friendly banter for rivals, warmth for underdogs, never sycophantic. When they name a coach, ask what specifically they take from that coach OR drop a relevant observation in your reply. NEVER ignore a rapport signal — it's the whole point of this stage.

CRITICAL — season_note field:
- Free-text season context the coach mentions (tournament name, league start date, "first match Saturday", etc.). Always set season_note when the coach mentions any temporal event.

CRITICAL — team_name handling:
- If current_profile.team_name is already set, ALWAYS return that exact same value. DO NOT alter it, abbreviate it, or replace it with a generic name (e.g. "Aurora FC U11", "FC U12", "Coach's Team"). The coach has already named the team — your job is to extract NEW info from coach_utterance, not re-state the team name.
- Only set team_name when the coach NAMES THE TEAM in coach_utterance for the first time. Examples: "we're called Monty FC", "team is Arsenal U12", "I coach Brighton Albion under-13".
- If current_profile.team_name is null AND coach_utterance doesn't mention a team name, return null. NEVER fall back to "Aurora FC <age>" — that placeholder is the app's job, not yours.

Prefer explicit coach wording over defaults. Do not invent missing team names.`;

export interface ExtractOnboardingOptions {
  /** Current onboarding stage — used for stage-aware extraction + reply. */
  stage?: OnboardingStage;
  /** Coach's first name (auth profile) — Sonnet uses it occasionally. */
  coachFirstName?: string | null;
  /** Coach user id — used to scope the AI analysis log row to a specific
   *  coach so we can reconstruct their full onboarding conversation later. */
  coachId?: string | null;
  /** The last Brain message shown to the coach. Lets the heuristic bridge
   *  cases where Brain offered to seed the roster and the coach replied with
   *  a bare "Yes" — without this, the extractor is stateless and the consent
   *  is silently lost. */
  lastBrainReply?: string | null;
}

/** Matches short, unqualified affirmatives — "Yes", "Yeah please", "Go ahead". */
const BARE_AFFIRMATIVE_RE = /^(?:(?:yes|yeah|yep|sure|ok(?:ay)?|please|alright|right|fine|go\s+ahead|do\s+it|just\s+do\s+it|sounds\s+good|works\s+for\s+me|that\s*'?s\s+fine|lovely|grand)[\s,.!]*){1,3}$/i;

/** Matches a Brain reply that *offered* to seed/populate placeholder players,
 *  so a follow-up "Yes" should be treated as accepting the offer. */
const BRAIN_OFFERED_ROSTER_SEED_RE = /\b(?:seed|populate|fill)\b[^.?!]{0,80}\b(?:roster|squad|placeholders?|test\s+(?:players?|names?)|stubs?|dummy|sample\s+(?:players?|names?))\b/i;

// ---------------------------------------------------------------------------
// AI analysis logging — per spec §08.7 "Log every call to AIAnalysisLog
// regardless of success or failure". Powers learning / debugging:
// reconstruct a coach's full onboarding conversation by querying
// ai_analysis_logs WHERE entity_type='onboarding' AND entity_id=<coachId>
// ORDER BY created_at.
// ---------------------------------------------------------------------------

/** Current prompt versions — bump when you change a system prompt so the
 *  log captures which version produced which response. */
const ONBOARDING_PROMPT_VERSION = '2026-05-20-mesocycle-v1';

interface OnboardingAnalysisLogInput {
  coachId?: string | null;
  stage?: OnboardingStage;
  analysisType:
    | 'onboarding_extract'
    | 'onboarding_save_success'
    | 'onboarding_derive_philosophy_curriculum';
  /** Raw coach utterance (or empty for non-utterance calls like derive). */
  utterance?: string;
  /** Snapshot of profile BEFORE this call (the AI's input context). */
  profileBefore?: OnboardingProfile;
  /** Snapshot of profile AFTER this call (what Brain learned / produced). */
  profileAfter?: OnboardingProfile;
  /** AI's composed conversational reply text. */
  reply?: string | null;
  /** Stage AFTER this call's cascade (so we can see the flow). */
  stageAfter?: OnboardingStage;
  success: boolean;
  /** Latency in ms — how long the AI call took. */
  latencyMs?: number;
  /** Error message if call failed. */
  errorMessage?: string;
}

async function logOnboardingAnalysis(input: OnboardingAnalysisLogInput): Promise<void> {
  // Fire-and-forget. Never throw — logging failures must not break the flow.
  try {
    const summary = input.reply
      ? input.reply.slice(0, 280)
      : input.errorMessage ?? `${input.analysisType} (no reply)`;
    const raw = {
      stage: input.stage ?? null,
      stage_after: input.stageAfter ?? null,
      utterance: input.utterance ?? null,
      profile_before: input.profileBefore ?? null,
      profile_after: input.profileAfter ?? null,
      reply: input.reply ?? null,
      latency_ms: input.latencyMs ?? null,
      error: input.errorMessage ?? null,
    };
    await supabase.from('ai_analysis_logs').insert({
      entity_type: 'onboarding',
      entity_id: input.coachId ?? null,
      analysis_type: input.analysisType,
      model: 'claude-opus-4-7',
      prompt_version: ONBOARDING_PROMPT_VERSION,
      response_summary: summary,
      raw_response: JSON.stringify(raw),
      success: input.success,
      requires_coach_review: false,
    });
  } catch (err) {
    console.warn('[onboarding] ai analysis log failed', err);
  }
}

export async function extractOnboardingInput(
  current: OnboardingProfile,
  text: string,
  options: ExtractOnboardingOptions | OnboardingStage = {},
): Promise<OnboardingExtractionResult> {
  // Back-compat: callers may have passed `stage` directly as the 3rd arg.
  const opts: ExtractOnboardingOptions =
    typeof options === 'string' ? { stage: options } : options;
  const stage = opts.stage;
  const clean = normalizeWhitespace(text);

  // Bridge: at the roster stage, if Brain offered to seed in the previous
  // reply and the coach answered with a bare affirmative, rewrite the
  // utterance so both the heuristic and the AI see the explicit consent.
  // Stateless extraction would otherwise drop the offer on the floor and
  // re-ask for names.
  const bridged =
    stage === 'roster'
      && current.roster === null
      && BARE_AFFIRMATIVE_RE.test(clean)
      && BRAIN_OFFERED_ROSTER_SEED_RE.test(opts.lastBrainReply ?? '')
      ? `${clean}. Seed the roster with placeholders.`
      : clean;

  const heuristic = applyOnboardingInput(current, bridged);
  const startedAt = Date.now();
  // Coach explicitly punted — skip the AI extraction so it can't override
  // the defaults applyOnboardingInput just filled in.
  if (isDefaultRequest(bridged)) {
    void logOnboardingAnalysis({
      coachId: opts.coachId,
      stage,
      analysisType: 'onboarding_extract',
      utterance: bridged,
      profileBefore: current,
      profileAfter: heuristic,
      reply: null,
      success: true,
      latencyMs: Date.now() - startedAt,
    });
    return { profile: heuristic, source: 'fallback' };
  }
  try {
    // Compute next-stage hint for the AI's reply — based on what the
    // heuristic learned so far, project ahead to the stage we'd advance to.
    const nextStageGuess = stage
      ? projectNextStageForReply(stage, heuristic)
      : null;
    // KB injection — pull coaching guidance relevant to the coach + current
    // stage so Brain's reply can be grounded in Ben's actual voice rather
    // than the model's generic football register. Selector returns [] at
    // pure fact-gathering stages (coach/team/roster/season), so this only
    // adds payload when it'll actually shape the reply.
    const kbContext = stage
      ? getRelevantSnippets(
          {
            coachLevel: heuristic.coachLevel,
            ageGroup: heuristic.ageGroup,
            methodology: heuristic.methodology,
            focus: heuristic.focus,
          },
          stage,
          'voice-onboarding-extract',
        )
      : [];
    const payload = await callAIJson<Record<string, unknown>>({
      system: ONBOARDING_EXTRACT_SYSTEM,
      userContent: JSON.stringify({
        coach_first_name: opts.coachFirstName ?? null,
        // Sanitised — defaults for stages the coach hasn't reached yet
        // are masked to null so Sonnet doesn't reference them as facts.
        current_profile: sanitiseProfileForPrompt(current),
        coach_utterance: bridged,
        current_stage: stage ?? null,
        stage_focus: stage ? STAGE_FOCUS_HINT[stage] : null,
        next_stage: nextStageGuess?.stage ?? null,
        next_stage_intent: nextStageGuess?.intent ?? null,
        kb_context: kbContext.map((s) => ({
          topic: s.topic,
          source: s.source,
          content: s.content,
        })),
      }),
      maxTokens: 700,
      surface: 'voice-onboarding',
      // surface default is Opus 4.7 — leave effort unset so it picks up.
    });
    if (!isRecord(payload)) {
      void logOnboardingAnalysis({
        coachId: opts.coachId,
        stage,
        analysisType: 'onboarding_extract',
        utterance: clean,
        profileBefore: current,
        profileAfter: heuristic,
        reply: null,
        success: false,
        latencyMs: Date.now() - startedAt,
        errorMessage: 'AI returned non-record payload',
      });
      return { profile: heuristic, source: 'fallback' };
    }
    const merged = mergeStructuredExtraction(heuristic, payload);
    const lifted = liftExtraNewFields(merged, payload);
    const reply = cleanString(payload.reply);
    void logOnboardingAnalysis({
      coachId: opts.coachId,
      stage,
      analysisType: 'onboarding_extract',
      utterance: clean,
      profileBefore: current,
      profileAfter: lifted,
      reply,
      success: true,
      latencyMs: Date.now() - startedAt,
    });
    return { profile: lifted, source: 'ai', reply };
  } catch (err) {
    console.warn('[web2 onboarding] structured extraction unavailable', err);
    void logOnboardingAnalysis({
      coachId: opts.coachId,
      stage,
      analysisType: 'onboarding_extract',
      utterance: clean,
      profileBefore: current,
      profileAfter: heuristic,
      reply: null,
      success: false,
      latencyMs: Date.now() - startedAt,
      errorMessage: err instanceof Error ? err.message : String(err),
    });
    return { profile: heuristic, source: 'fallback' };
  }
}

export interface ComposeDoneReplyInput {
  profile: OnboardingProfile;
  sessionCount: number;
  coachFirstName?: string | null;
  coachId?: string | null;
  /** When the save fell back to localStorage instead of the DB (e.g. offline
   *  or placeholder env). Brain mentions it lightly. */
  localOnly?: boolean;
}

/** Generate the "save succeeded" reply in Brain's voice. Sonnet weighs the
 *  team context + what to do next; on failure we drop back to a richer
 *  hand-written line that still offers next steps. */
export async function composeSaveSuccessReply(input: ComposeDoneReplyInput): Promise<string> {
  const startedAt = Date.now();
  // At the 'done' stage the selector returns trust-building + individual-
  // coaching snippets — relevant scaffolding for the sign-off and the
  // implicit next-action prompt even though the reply is intentionally
  // short. Capped at 2 via 'voice-onboarding-save' surface.
  const kbContext = getRelevantSnippets(
    {
      coachLevel: input.profile.coachLevel,
      ageGroup: input.profile.ageGroup,
      methodology: input.profile.methodology,
      focus: input.profile.focus,
    },
    'done',
    'voice-onboarding-save',
  );
  try {
    const payload = await callAIJson<{ reply?: unknown }>({
      system: SAVE_SUCCESS_SYSTEM,
      userContent: JSON.stringify({
        coach_first_name: input.coachFirstName ?? null,
        profile: input.profile,
        session_count: input.sessionCount,
        local_only: !!input.localOnly,
        next_actions: [
          'open the calendar to see the Week 1 sessions',
          'jump into designing the first session in detail',
          'add real player names to replace the placeholders',
          'set the team\'s playing principles before training starts',
        ],
        kb_context: kbContext.map((s) => ({
          topic: s.topic,
          source: s.source,
          content: s.content,
        })),
      }),
      maxTokens: 240,
      surface: 'voice-onboarding',
    });
    const text = typeof payload?.reply === 'string' ? payload.reply.trim() : '';
    if (text.length > 0) {
      void logOnboardingAnalysis({
        coachId: input.coachId,
        stage: 'done',
        analysisType: 'onboarding_save_success',
        profileBefore: input.profile,
        profileAfter: input.profile,
        reply: text,
        success: true,
        latencyMs: Date.now() - startedAt,
      });
      return text;
    }
  } catch (err) {
    console.warn('[web2 onboarding] save-success reply unavailable', err);
    void logOnboardingAnalysis({
      coachId: input.coachId,
      stage: 'done',
      analysisType: 'onboarding_save_success',
      profileBefore: input.profile,
      profileAfter: input.profile,
      reply: null,
      success: false,
      latencyMs: Date.now() - startedAt,
      errorMessage: err instanceof Error ? err.message : String(err),
    });
  }
  // Fallback — still always offers a next step.
  const name = input.profile.teamName ?? 'your team';
  const localNote = input.localOnly
    ? ' Stored on this device for now — it\'ll sync the moment you\'re online.'
    : '';
  const fallback = `Done — ${name} is in.${localNote} Opening the calendar now.`;
  void logOnboardingAnalysis({
    coachId: input.coachId,
    stage: 'done',
    analysisType: 'onboarding_save_success',
    profileBefore: input.profile,
    profileAfter: input.profile,
    reply: fallback,
    success: true,
    latencyMs: Date.now() - startedAt,
  });
  return fallback;
}

/** Sonnet-derived Philosophy + Curriculum based on the full onboarding
 *  profile. Called when the coach lands on (or skips past) the philosophy
 *  stage. Returns both artefacts in one round trip. */
export async function derivePhilosophyAndCurriculum(
  profile: OnboardingProfile,
  coachFirstName: string | null = null,
  coachId: string | null = null,
): Promise<{ philosophy: PhilosophyDraft; curriculum: CurriculumDraft }> {
  // Mesocycle is fixed at 4-6 weeks regardless of what was captured at the
  // `plan` stage. If the coach said "two weeks" Brain still draws a 4-week
  // mesocycle — the push-back is part of the design, not a bug.
  const weeks = clampMesocycleWeeks(profile.weeksToPlan ?? 4);
  const fallback = buildHeuristicPhilosophyAndCurriculum(profile, weeks);
  const startedAt = Date.now();
  // Pull KB chunks relevant to this coach + the curriculum-derivation
  // moment. Each chunk is a distilled snippet of Ben Petty's coaching
  // guidance — Opus uses them to ground its output in real coaching
  // rather than the textbook examples it might otherwise generate.
  const kbContext = getRelevantSnippets(
    {
      coachLevel: profile.coachLevel,
      ageGroup: profile.ageGroup,
      methodology: profile.methodology,
      focus: profile.focus,
    },
    isImplicitPhilosophyCoach(profile) ? 'curriculum' : 'philosophy',
    'voice-onboarding-derive',
  );
  try {
    const payload = await callAIJson<Record<string, unknown>>({
      system: DERIVE_PHILOSOPHY_SYSTEM,
      userContent: JSON.stringify({
        coach_first_name: coachFirstName,
        profile,
        weeks_to_plan: weeks,
        is_academy: !isImplicitPhilosophyCoach(profile),
        kb_context: kbContext.map((s) => ({
          topic: s.topic,
          source: s.source,
          content: s.content,
        })),
      }),
      maxTokens: 1400,
      surface: 'voice-onboarding',
    });
    const parsed = parseDerivedPhilosophyCurriculum(payload, weeks, profile);
    const result = parsed ?? fallback;
    void logOnboardingAnalysis({
      coachId,
      stage: isImplicitPhilosophyCoach(profile) ? 'confirm' : 'philosophy',
      analysisType: 'onboarding_derive_philosophy_curriculum',
      profileBefore: profile,
      profileAfter: { ...profile, philosophy: result.philosophy, curriculum: result.curriculum },
      reply: `Mesocycle: ${result.curriculum.mesocycle.weekCount}-week ${result.curriculum.mesocycle.phaseEmphasis} — ${result.curriculum.mesocycle.headline}`,
      success: parsed !== null,
      latencyMs: Date.now() - startedAt,
      errorMessage: parsed === null ? 'Parse failed — using heuristic fallback' : undefined,
    });
    return result;
  } catch (err) {
    console.warn('[web2 onboarding] philosophy/curriculum derivation unavailable', err);
    void logOnboardingAnalysis({
      coachId,
      stage: isImplicitPhilosophyCoach(profile) ? 'confirm' : 'philosophy',
      analysisType: 'onboarding_derive_philosophy_curriculum',
      profileBefore: profile,
      profileAfter: { ...profile, philosophy: fallback.philosophy, curriculum: fallback.curriculum },
      reply: `Mesocycle (heuristic fallback): ${fallback.curriculum.mesocycle.weekCount}-week ${fallback.curriculum.mesocycle.phaseEmphasis} — ${fallback.curriculum.mesocycle.headline}`,
      success: false,
      latencyMs: Date.now() - startedAt,
      errorMessage: err instanceof Error ? err.message : String(err),
    });
    return fallback;
  }
}

const DERIVE_PHILOSOPHY_SYSTEM = `You are Brain — the same senior English football coach. The coach has finished telling you about themselves, their team, their season, their aims, and how many weeks they want planned. Now draft them a starting Philosophy + Curriculum that reflects WHO THEY ARE — not a generic template. Use their methodology phrase, their admired_coaches, their played_to, their supports_team, the age group, the squad size — all of it.

UK English. Plain coaching language. Touchline-friendly principles, not textbook prose. The Bielsa fan gets pressing-flavoured principles; the Klopp fan gets transition energy; the Pep fan gets positional build-up; an under-eleven coach focused on "team organisation" gets simple shape principles ("stay compact", "talk to the keeper").

GROUND YOUR OUTPUT IN THE KB CONTEXT:

The userContent below includes a "kb_context" array — distilled chunks of Ben Petty's actual coaching guidance from his voice notes. THIS IS YOUR PRIMARY SOURCE. Treat the kb_context like a senior coach handing you their playbook:
- The mesocycle structure (single theme 4–6 weeks), the defending pedagogy (press / show outside / help your mate), the grassroots session shape (10–15 min blocks, ball-each, 1v1-rich SSGs), the five common mistakes, the individual-vs-team coaching distinction — all of these come from kb_context. Read it carefully and let it shape your output.
- If kb_context disagrees with your own training, kb_context wins.
- Quote phrases from kb_context where they fit naturally — Ben's coaching language is more grounded than textbook English.
- If kb_context doesn't cover something the coach mentioned (e.g. an unusual age group, a specific opponent), use your own judgement, but never contradict the KB.

The mesocycle principle in kb_context is non-negotiable: ONE phase emphasis across the WHOLE cycle. Variety inside the mesocycle comes from SUB-THEMES and CONSTRAINTS, not from changing the theme. Pick the phase emphasis (attacking / defending / transitions / individual) that matches the coach's methodology phrase and STICK WITH IT every week.

If kb_context contains "grassroots_session_design", every drill / sub-theme you propose must satisfy: short blocks (10–15 min), ball-each-or-pairs default, 1v1 emphasis, SSG-driven coaching (rules drive behaviour), high activity (no static drills / queues).

If kb_context contains "defending_pedagogy" and the coach picked defending as their focus, the 3 canonical principles (press the ball / protect the middle and show outside / help your mate) MUST appear in the defend section of the philosophy, and the curriculum sub-themes MUST be drawn from 1v1 / 2v2 / 3v2 / 2v3 SSG variants.

Return ONLY valid JSON in this schema:

{
  "philosophy": {
    "attack":     [ { "text": string, "cornerTags": string[] }, ... ],
    "defend":     [ { "text": string, "cornerTags": string[] }, ... ],
    "transition": [ { "text": string, "cornerTags": string[] }, ... ],
    "restart":    [ { "text": string, "cornerTags": string[] }, ... ]
  },
  "curriculum": {
    "mesocycle": {
      "weekCount": number (4-6),
      "headline": string (6-12 words, the coach's methodology pulled forward),
      "phaseEmphasis": "attacking" | "defending" | "transitions" | "individual",
      "primaryPrincipleRefs": [ { "phase": "attack"|"defend"|"transition"|"restart", "index": number } ],
      "weeks": [
        {
          "week": number (1-indexed),
          "subTheme": string (4-8 words, a slice of the headline),
          "constraints": [ string, ... ] (1-3 SSG / drill rules that drive this week's sub-theme),
          "principleRefs": [ { "phase": "attack"|"defend"|"transition"|"restart", "index": number } ]
        }, ...
      ]
    }
  }
}

CRITICAL rules:
1. ALL 4 philosophy phases (attack, defend, transition, restart) must have AT LEAST ONE principle. Two is ideal for grassroots. Three is fine for academy. Don't pad — quality over count.
2. Psych-Social Foundation Rule: every principle MUST include at least one of "corner-psych" or "corner-social" in its cornerTags array. (Other valid tags: "corner-technical", "corner-physical".)
3. weekCount MUST equal weeks_to_plan AND must be between 4 and 6. If weeks_to_plan is outside 4-6, clamp it: <4 becomes 4, >6 becomes 6.
4. phaseEmphasis is SINGULAR across the entire mesocycle. ALL weeks share the same phase. Do not vary it.
5. Each principle "text" is what a coach would shout at training — 6 to 14 words. NOT a textbook sentence. NOT "players should...". Use imperatives or short statements: "Stay compact when we lose it" / "First touch towards space" / "Second ball is ours" / "Press the ball, screen the pass".
6. Sub-themes progress sensibly — start with the most foundational slice (individual technique / body shape / start pattern), layer pair-work in week 2-3, synthesise in the final week. NEVER make a week a generic placeholder like "consolidation week".
7. Constraints are SSG / drill rules a coach could actually run on the pitch ("nearest defender must press", "two-touch limit", "one switch per build"). Avoid abstract phrases like "focus on transitions".
8. Don't invent specifics the coach didn't mention. If they said "team organisation" you give them organisation-flavoured sub-themes, not "play out from the back" sub-themes.
9. Reflect the rapport: a Liverpool fan whose admired coach is Klopp gets high-energy, counter-press-flavoured sub-themes. A coach who said "be hard to beat" gets compact defending + recovery-run sub-themes, not Pep-style positional.
10. No markdown. No prose outside the JSON.`;

interface RawPrinciple {
  text?: unknown;
  cornerTags?: unknown;
}

interface RawWeek {
  week?: unknown;
  subTheme?: unknown;
  constraints?: unknown;
  principleRefs?: unknown;
}

interface RawMesocycle {
  weekCount?: unknown;
  headline?: unknown;
  phaseEmphasis?: unknown;
  primaryPrincipleRefs?: unknown;
  weeks?: unknown;
}

const VALID_CORNER_TAGS: CornerId[] = ['corner-technical', 'corner-physical', 'corner-psych', 'corner-social'];
const PHASE_IDS: PhaseId[] = ['attack', 'defend', 'transition', 'restart'];

/** Mesocycle range — Ben's rule. weeksToPlan gets clamped here regardless
 *  of what the AI returns or what the coach asked for. */
const MIN_MESOCYCLE_WEEKS = 4;
const MAX_MESOCYCLE_WEEKS = 6;

function clampMesocycleWeeks(n: number): number {
  if (!Number.isFinite(n)) return MIN_MESOCYCLE_WEEKS;
  if (n < MIN_MESOCYCLE_WEEKS) return MIN_MESOCYCLE_WEEKS;
  if (n > MAX_MESOCYCLE_WEEKS) return MAX_MESOCYCLE_WEEKS;
  return Math.floor(n);
}

/** Pick a sensible phaseEmphasis fallback from the coach profile when the
 *  AI didn't return a valid one. Uses the coach's `focus` field which was
 *  set during the `aims` stage. */
function fallbackPhaseFromProfile(p: OnboardingProfile): PhaseOfPlay {
  return p.focus;
}

function parsePrincipleRefs(
  raw: unknown,
  philosophy: PhilosophyDraft,
): Array<{ phase: PhaseId; index: number }> {
  if (!Array.isArray(raw)) return [];
  const refs: Array<{ phase: PhaseId; index: number }> = [];
  for (const r of raw) {
    if (!isRecord(r)) continue;
    const phase = typeof r.phase === 'string' && (PHASE_IDS as string[]).includes(r.phase) ? (r.phase as PhaseId) : null;
    const idx = typeof r.index === 'number' && r.index >= 0 ? Math.floor(r.index) : null;
    if (phase != null && idx != null && idx < philosophy[phase].length) {
      refs.push({ phase, index: idx });
    }
  }
  return refs;
}

function parseDerivedPhilosophyCurriculum(
  payload: unknown,
  weeksExpected: number,
  profile: OnboardingProfile,
): { philosophy: PhilosophyDraft; curriculum: CurriculumDraft } | null {
  if (!isRecord(payload)) return null;
  const phRaw = payload.philosophy;
  const cuRaw = payload.curriculum;
  if (!isRecord(phRaw) || !isRecord(cuRaw)) return null;

  const philosophy: PhilosophyDraft = {
    attack: parsePrincipleList(phRaw.attack),
    defend: parsePrincipleList(phRaw.defend),
    transition: parsePrincipleList(phRaw.transition),
    restart: parsePrincipleList(phRaw.restart),
  };
  // All four phases must have at least one principle.
  if (
    philosophy.attack.length === 0 ||
    philosophy.defend.length === 0 ||
    philosophy.transition.length === 0 ||
    philosophy.restart.length === 0
  ) return null;

  const mesoRaw = cuRaw.mesocycle as RawMesocycle | undefined;
  if (!isRecord(mesoRaw)) return null;

  const weekCount = clampMesocycleWeeks(
    typeof mesoRaw.weekCount === 'number' ? mesoRaw.weekCount : weeksExpected,
  );
  const headline = cleanString(mesoRaw.headline) ?? (profile.methodology ?? 'develop the group');
  const phaseEmphasis = isPhaseOfPlay(mesoRaw.phaseEmphasis)
    ? mesoRaw.phaseEmphasis
    : fallbackPhaseFromProfile(profile);
  const primaryPrincipleRefs = parsePrincipleRefs(mesoRaw.primaryPrincipleRefs, philosophy);

  const weeksRaw = mesoRaw.weeks;
  if (!Array.isArray(weeksRaw)) return null;
  const weeks: CurriculumWeek[] = [];
  for (let i = 0; i < weeksRaw.length; i++) {
    const w = weeksRaw[i] as RawWeek;
    if (!isRecord(w)) continue;
    const wkNum = typeof w.week === 'number' ? w.week : i + 1;
    const subTheme = cleanString(w.subTheme);
    const constraintsRaw = Array.isArray(w.constraints) ? w.constraints : [];
    const constraints = (constraintsRaw as unknown[])
      .filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
      .map((c) => c.trim());
    const refs = parsePrincipleRefs(w.principleRefs, philosophy);
    if (subTheme) {
      weeks.push({ week: wkNum, subTheme, constraints, principleRefs: refs });
    }
  }
  if (weeks.length === 0) return null;

  // Trim / pad to exactly weekCount.
  while (weeks.length > weekCount) weeks.pop();
  while (weeks.length < weekCount) {
    const last = weeks[weeks.length - 1];
    weeks.push({
      week: weeks.length + 1,
      subTheme: `Layered ${last.subTheme.toLowerCase()}`,
      constraints: last.constraints,
      principleRefs: last.principleRefs,
    });
  }

  return {
    philosophy,
    curriculum: {
      mesocycle: {
        weekCount,
        headline,
        phaseEmphasis,
        primaryPrincipleRefs: primaryPrincipleRefs.length > 0
          ? primaryPrincipleRefs
          : weeks[0]?.principleRefs ?? [],
        weeks,
      },
    },
  };
}

function parsePrincipleList(raw: unknown): PhilosophyPrinciple[] {
  if (!Array.isArray(raw)) return [];
  const out: PhilosophyPrinciple[] = [];
  for (const item of raw as RawPrinciple[]) {
    if (!isRecord(item)) continue;
    const text = cleanString(item.text);
    if (!text) continue;
    const tagsRaw = Array.isArray(item.cornerTags) ? item.cornerTags : [];
    const tags = (tagsRaw as unknown[])
      .filter((t): t is CornerId => typeof t === 'string' && (VALID_CORNER_TAGS as string[]).includes(t));
    // Psych-Social Foundation Rule.
    const hasPsychSocial = tags.includes('corner-psych') || tags.includes('corner-social');
    const finalTags = hasPsychSocial ? tags : [...tags, 'corner-psych'] as CornerId[];
    out.push({ text, cornerTags: finalTags });
  }
  return out;
}

/** Last-ditch fallback when the AI call fails. Builds a sensible default
 *  mesocycle philosophy + curriculum from the profile's methodology +
 *  focus + weeks. Always single-theme — same rule as the AI prompt. */
function buildHeuristicPhilosophyAndCurriculum(
  p: OnboardingProfile,
  weeks: number,
): { philosophy: PhilosophyDraft; curriculum: CurriculumDraft } {
  const weekCount = clampMesocycleWeeks(weeks);
  const headline = p.methodology ?? 'team organisation';
  const phase = fallbackPhaseFromProfile(p);
  const ps: PhilosophyDraft = {
    attack: [
      { text: 'Move the ball forward when it\'s on', cornerTags: ['corner-technical', 'corner-psych'] },
      { text: 'First touch towards space', cornerTags: ['corner-technical', 'corner-social'] },
    ],
    defend: [
      { text: 'Stay compact when we lose it', cornerTags: ['corner-social', 'corner-physical'] },
      { text: 'Talk to the player next to you', cornerTags: ['corner-social', 'corner-psych'] },
    ],
    transition: [
      { text: 'Second ball is ours — react quick', cornerTags: ['corner-psych', 'corner-physical'] },
      { text: 'Get organised the moment we win it', cornerTags: ['corner-social', 'corner-psych'] },
    ],
    restart: [
      { text: 'Set up before the whistle', cornerTags: ['corner-social', 'corner-psych'] },
      { text: 'Know your job at every restart', cornerTags: ['corner-psych', 'corner-technical'] },
    ],
  };

  // Sub-theme arc per phase emphasis — mesocycle slices that layer
  // complexity. First entry = foundation, last = synthesis.
  const SUB_THEMES_BY_PHASE: Record<PhaseOfPlay, Array<{ subTheme: string; constraints: string[] }>> = {
    defending: [
      { subTheme: 'Body shape + 1v1 defending', constraints: ['1v1 channels with a goal each end', 'defender wins by holding shape for 8 seconds'] },
      { subTheme: 'Pair defending — cover + balance', constraints: ['2v2 with offside line', 'covering defender must call the press'] },
      { subTheme: 'Team shape to press the ball', constraints: ['7v7 with a press trigger rule', 'nearest player to ball must engage'] },
      { subTheme: 'Recovery runs + second balls', constraints: ['transition game with a 6-second counter window'] },
      { subTheme: 'Compact mid-block under pressure', constraints: ['9v9 with a no-pass-back rule for 30s windows'] },
      { subTheme: 'Synthesis in 9v9 — all layers together', constraints: ['normal 9v9 rules, no extras', 'review shape + press in the team talk'] },
    ],
    attacking: [
      { subTheme: 'First touch + receiving on the half-turn', constraints: ['rondo 4v2 with first-touch direction rule'] },
      { subTheme: 'Combining in tight spaces', constraints: ['7v4 with a two-touch limit'] },
      { subTheme: 'Third-man runs + switches of play', constraints: ['8v6 with a mandatory third-man pass to score'] },
      { subTheme: 'Build-up patterns under press', constraints: ['9v7 with the opposition pressing on a trigger'] },
      { subTheme: 'Final-third combinations', constraints: ['attack-vs-defence with a wide-channel rule'] },
      { subTheme: 'Synthesis in 9v9 — build-to-finish', constraints: ['normal 9v9 with a build-from-keeper start rule'] },
    ],
    transitions: [
      { subTheme: 'React quick when we lose it', constraints: ['transition game with a 6-second counter-press window'] },
      { subTheme: 'Counter-attack with intent', constraints: ['waves with a 4-pass-to-shot limit on transition'] },
      { subTheme: 'Rest defence + recovery', constraints: ['9v9 with a recovery line — defenders must get behind it'] },
      { subTheme: 'Switching gears on regain', constraints: ['turnover game — first 8s after winning ball is high tempo'] },
      { subTheme: 'Game management on both transitions', constraints: ['score the next goal wins, transition rules apply'] },
      { subTheme: 'Synthesis in 9v9 — both directions live', constraints: ['normal 9v9 with transition coaching only'] },
    ],
    individual: [
      { subTheme: '1v1 ball mastery + close control', constraints: ['ball each, 90 seconds work / 30 rest, no queues'] },
      { subTheme: 'Receiving + first touch under pressure', constraints: ['ball between two with a passive defender'] },
      { subTheme: 'Scanning + decision-making on the ball', constraints: ['rondo with a colour-call rule before the pass'] },
      { subTheme: 'Finishing under pressure', constraints: ['1v1 vs keeper, alternate angles, 60s clock'] },
      { subTheme: 'Match-realistic 1v1 in tight spaces', constraints: ['1v1 in a 6x6 grid, 4 grids running at once'] },
      { subTheme: 'Synthesis in small-sided games', constraints: ['4v4 with no offside, all skills live'] },
    ],
  };
  const arc = SUB_THEMES_BY_PHASE[phase];
  // Pick a contiguous slice of the arc: foundation slices first, synthesis last.
  const picked: Array<{ subTheme: string; constraints: string[] }> = [];
  for (let i = 0; i < weekCount - 1; i++) picked.push(arc[i] ?? arc[arc.length - 2]);
  picked.push(arc[arc.length - 1]); // synthesis always last

  const phaseKey: PhaseId =
    phase === 'defending' ? 'defend'
      : phase === 'attacking' ? 'attack'
      : phase === 'transitions' ? 'transition'
      : 'attack';

  const weekly: CurriculumWeek[] = picked.map((slice, i) => ({
    week: i + 1,
    subTheme: slice.subTheme,
    constraints: slice.constraints,
    principleRefs: [{ phase: phaseKey, index: 0 }],
  }));

  return {
    philosophy: ps,
    curriculum: {
      mesocycle: {
        weekCount,
        headline,
        phaseEmphasis: phase,
        primaryPrincipleRefs: [{ phase: phaseKey, index: 0 }],
        weeks: weekly,
      },
    },
  };
}

const SAVE_SUCCESS_SYSTEM = `You are Brain — the same senior English football coach as before. Same voice: dry, understated, UK English, no Americanisms (NEVER use "awesome", "you got this", soccer/field/roster/game). Adapt to the rapport signals in the profile when they tell you something.

The coach has just confirmed. The team and their first sessions are saved. The app is about to auto-navigate them to the calendar in 3 seconds — your reply needs to be a brief sign-off, NOT a chatty summary.

Compose ONE to TWO sentences max:
- Confirm in one beat ("Done — {team} is in.") — reference the team name ONLY.
- Mention that the calendar is opening shortly.
- DO NOT restate the full setup (age group, squad size, weeks, philosophy, mesocycle, drills, formation, training days). The coach just lived through that — they don't need it repeated.
- DO NOT ask a question — the auto-navigation handles the next step.
- DO NOT trail into "what would you like to do next" — they're being taken to the calendar.

Example good replies:
- "Done — Monty are in. Opening the calendar now."
- "Right, U11 Owls are locked in. Calendar loading in a couple of seconds."
- "Saved. Taking you to the calendar to see Week 1."

Plain spoken English. UK only. No markdown. No lists. No "lads".

If the userContent contains a non-empty "kb_context" array, your sign-off can lean on Ben Petty's vocabulary subtly (e.g. "right, that's Week 1 ready — the cycle starts there"), but DO NOT recite KB content, DO NOT lecture, and DO NOT extend the reply beyond 2 sentences just because there's context. The context exists to ground tone, not to be quoted.

Return ONLY valid JSON: { "reply": string }.`;

/** Given the current stage and the post-extraction profile, project ahead
 *  to the stage Brain will most likely lead the coach into in the reply.
 *  Used purely as a hint to Sonnet — the actual cascade still runs in the
 *  UI after extraction. */
function projectNextStageForReply(
  stage: OnboardingStage,
  p: OnboardingProfile,
): { stage: OnboardingStage; intent: string } | null {
  let cursor = stage;
  while (cursor !== 'confirm' && cursor !== 'done' && isStageComplete(cursor, p)) {
    cursor = nextStage(cursor);
  }
  if (cursor === stage) {
    // Stage not yet complete — Brain stays here and re-prompts gently.
    return { stage, intent: STAGE_FOCUS_HINT[stage] };
  }
  return { stage: cursor, intent: STAGE_FOCUS_HINT[cursor] };
}

/** Sanitise the profile before sending it to Sonnet — masks default values
 *  for fields the coach hasn't actually been asked about yet. Without this,
 *  Sonnet treats things like `focus: 'attacking'` (a default) and
 *  `trainingDaysPerWeek: 2` (a default) as captured facts and references them
 *  in its replies, leading to hallucinations ("you're working with attacking
 *  football in sixty-minute sessions twice a week" when the coach never said
 *  any of that).
 *
 *  Rule: if the relevant stage hasn't been visited and the field still holds
 *  its default value, send null. Once the coach has answered the stage, the
 *  value is real and we pass it through.
 */
function sanitiseProfileForPrompt(p: OnboardingProfile): Record<string, unknown> {
  const focusPrescribed = p.focusRationale !== null;
  const scheduleAnswered = p.trainingDays !== null && p.trainingStartTime !== null;
  return {
    ...p,
    // `focus` is only meaningful once Brain has PRESCRIBED it at the aims
    // stage (focusRationale set). Before then it's just the DEFAULT_PROFILE
    // seed and surfacing it caused Sonnet to invent the phase.
    focus: focusPrescribed ? p.focus : null,
    // Schedule fields surface only once the schedule stage is answered.
    trainingDays: scheduleAnswered ? p.trainingDays : null,
    trainingStartTime: scheduleAnswered ? p.trainingStartTime : null,
    trainingDaysPerWeek: scheduleAnswered ? p.trainingDaysPerWeek : null,
    sessionDurationMins: scheduleAnswered ? p.sessionDurationMins : null,
    // coachLevel defaults to 'GRASSROOTS' — but it's also set via
    // inferCoachLevel on team-stage utterances. Only surface it once the
    // team stage has produced ANY signal.
    coachLevel: p.teamName !== null || p.ageGroup !== null ? p.coachLevel : null,
  };
}

/** Per-stage focus hint injected into the AI prompt context. Tells Haiku
 *  which field the coach is most likely answering right now so it extracts
 *  it aggressively rather than missing a freeform phrase. */
const STAGE_FOCUS_HINT: Record<OnboardingStage, string> = {
  coach:   'Coach was asked rapport questions: how long coaching, did you play, coaches you admire, team you support, what you enjoy about coaching. Capture years_coaching, badges, played_to, admired_coaches, supports_team, enjoys, frustrations from whatever they said. If they only answered one or two, warmly ask ONE more rapport question — choose POSITIVE leads ("what do you most enjoy about coaching this group?", "any coaches whose style stuck with you that we haven\'t covered?", "favourite age group to coach?"). NEVER lead with negative framings like "what winds you up" or "what frustrates you most" — only engage with frustrations if the coach surfaces them first.',
  team:    'Coach was asked: team name, age group, squad size, league, level, formation. Capture all that apply.',
  roster:  'Coach was asked to list players or skip. Names will appear as a comma-separated list.',
  season:  'Coach was asked where they are in the season. Capture season_phase and season_note (any tournament / fixture / start date mentioned).',
  aims:    'Coach gave you a brief. Your job is to PRESCRIBE, not parrot. Take the brief + the coach profile (age group, level, years coaching, admired coaches) + any KB excerpts in kb_context and synthesise a coaching answer:\n  1. methodology — a short, sharp HEADLINE (3-7 words, coach-facing, e.g. "ball mastery under light pressure", "compact mid-block defending", "build-up from the keeper"). Use the coach\'s brief as the seed but elevate it — never just echo "team organization" verbatim.\n  2. methodology_principles — exactly 3 concrete principles that operationalise the headline. Each principle is 5-10 words, what-to-look-for-on-the-grass, e.g. "two-touch maximum in tight space", "first touch out of pressure", "press the back-pass trigger".\n  3. methodology_rationale — ONE sentence (max 25 words) saying WHY this is the right block for THIS coach\'s team. Reference age group + level + brief.\n  4. primary_phase — pick the phase-of-play that fits the prescription: attacking / defending / transitions / individual. For U7-U10 grassroots default to "individual" unless brief clearly indicates otherwise; for older or competitive default to "attacking" or whatever the brief points at.\n  5. focus_rationale — ONE sentence (max 20 words) saying why this phase comes first in the mesocycle.\nReply naturally: present the headline + principles as a recommendation ("Right — for U11 grassroots with organisation as your headline I\'d set you up with [headline]. Three things to chase: [principle 1], [principle 2], [principle 3]. Why? [rationale]. Sound right?"). NEVER use the bare verbatim brief as the methodology.',
  schedule: 'Coach is being asked which nights they train and what time + how long each session is. Extract training_days (3-letter weekday codes Mon/Tue/Wed/Thu/Fri/Sat/Sun in array), training_start_time (HH:MM 24-hour), session_duration_mins (30-120). If the coach gives only one (e.g. "Tuesdays"), capture that and prompt for the missing pieces. Sensible UK grassroots defaults: Tue+Thu, 18:30, 60 min — but ALWAYS confirm before assuming. If coach says "twice a week" without specifying days, suggest Tue+Thu and ask if that fits.',
  plan:    'Coach was asked how many weeks of sessions to draft. The mesocycle is fixed at 4-6 weeks (Ben Petty rule: anything less than 4 and you can\'t see learning; anything more than 6 and the kids get bored). If the coach says fewer than 4, gently say so and clamp to 4. If more than 6, suggest splitting into two mesocycles and clamp this one to 6.',
  philosophy: 'Coach is reviewing the philosophy draft you produced. Their utterance is either an acceptance ("yes", "looks right") OR a revision of a specific principle. If they want a revision, capture the methodology field with the new emphasis.',
  curriculum: 'Coach is reviewing the MESOCYCLE curriculum draft you produced — single theme across 4-6 weeks, variety via sub-themes inside. Acceptance moves them on. If they ask to "alternate themes week by week" or "rotate phases", push back: that pattern stops them seeing whether anything is landing. Suggest they keep the single theme for this block and queue the next mesocycle. Revisions to specific sub-themes or constraints are fine — capture them.',
  confirm: 'Coach is reviewing the summary. They will say yes to confirm or describe a change.',
  walkthrough: 'Coach has just confirmed the setup. Brain has the calendar drafted and is offering to read through Week 1 session-by-session. The coach will either say "yes/walk me through it" (advance walkthrough), "next" (move to next session), "open the calendar" / "I\'ll have a look" (end walkthrough), or ask a question about a specific session. Stay warm and concise — short narrations, never lecture.',
  done:    'Onboarding complete.',
};

function liftExtraNewFields(
  base: OnboardingProfile,
  payload: Record<string, unknown>,
): OnboardingProfile {
  const seasonPhaseRaw = payload.season_phase;
  const validSeasonPhases: SeasonPhase[] = ['pre_season', 'early', 'mid', 'late', 'playoffs', 'offseason'];
  const seasonPhase = typeof seasonPhaseRaw === 'string' && (validSeasonPhases as string[]).includes(seasonPhaseRaw)
    ? (seasonPhaseRaw as SeasonPhase)
    : base.seasonPhase;
  return {
    ...base,
    badges: cleanString(payload.badges) ?? base.badges,
    playedTo: cleanString(payload.played_to) ?? base.playedTo,
    admiredCoaches: cleanString(payload.admired_coaches) ?? base.admiredCoaches,
    supportsTeam: cleanString(payload.supports_team) ?? base.supportsTeam,
    enjoys: cleanString(payload.enjoys) ?? base.enjoys,
    frustrations: cleanString(payload.frustrations) ?? base.frustrations,
    leagueName: cleanString(payload.league_name) ?? base.leagueName,
    seasonPhase,
    seasonNote: cleanString(payload.season_note) ?? base.seasonNote,
    weeksToPlan: cleanNumber(payload.weeks_to_plan, 1, 24) ?? base.weeksToPlan,
  };
}

/** Last-resort capture: when AI + regex extraction leave the current stage
 *  incomplete but the coach clearly answered (non-trivial, non-meta), store
 *  the raw utterance into the stage's primary free-text field. Guarantees
 *  Brain doesn't keep asking the same question after a real answer.
 *
 *  Returns the patched profile. Callers should run this AFTER `extractOnboardingInput`
 *  but BEFORE the stage completeness check + cascade. */
export function softCaptureForStage(
  stage: OnboardingStage,
  profile: OnboardingProfile,
  raw: string,
): OnboardingProfile {
  const trimmed = raw.trim();
  if (trimmed.length < 2) return profile;
  if (isSkipRequest(trimmed) || isOnboardingConfirmation(trimmed)) return profile;
  if (detectEditIntent(trimmed)) return profile;

  switch (stage) {
    case 'aims':
      // Any free-text answer at the aims stage IS the methodology.
      if (!profile.methodology) {
        return { ...profile, methodology: trimmed };
      }
      return profile;
    case 'season':
      if (!profile.seasonPhase) {
        return {
          ...profile,
          seasonPhase: 'mid',
          seasonNote: profile.seasonNote ? `${profile.seasonNote}; ${trimmed}` : trimmed,
        };
      }
      return profile;
    case 'coach': {
      // If the coach gave a non-trivial answer but no specific rapport
      // signal could be parsed, capture it as either enjoys or frustrations
      // (whichever slot is open) so the stage can still advance. Keeps the
      // coach's words in the profile so Brain can refer back to them.
      const signals = [
        profile.yearsCoaching != null,
        profile.badges != null,
        profile.playedTo != null,
        profile.admiredCoaches != null,
        profile.supportsTeam != null,
        profile.enjoys != null,
        profile.frustrations != null,
      ].filter(Boolean).length;
      if (signals === 0) {
        // Empty coach card — treat the utterance as a bio note.
        return {
          ...profile,
          badges: profile.badges ?? (trimmed.length > 60 ? trimmed.slice(0, 58) + '…' : trimmed),
        };
      }
      return profile;
    }
    case 'plan':
      // No fallback — weeksToPlan is numeric. Brain will re-ask.
      return profile;
    case 'schedule':
      // No fallback — schedule fields are typed. Brain will re-ask if the
      // coach's utterance didn't carry a parseable day or time.
      return profile;
    case 'team':
    case 'roster':
    case 'philosophy':
    case 'curriculum':
    case 'confirm':
    case 'walkthrough':
    case 'done':
      return profile;
  }
}

export function onboardingReply(profile: OnboardingProfile): string {
  const missing: string[] = [];
  if (!profile.ageGroup) missing.push('age group');
  if (!profile.playerCount) missing.push('squad size');
  if (!profile.methodology) missing.push('how you want them to play');

  if (missing.length > 0) {
    return `Got it. I have ${profile.ageGroup ?? 'the age group pending'}, ${profile.playerCount ? `${profile.playerCount} players` : 'squad size pending'}, and ${PHASE_OF_PLAY_META[profile.focus].label.toLowerCase()} as the first football focus. Tell me ${missing[0]} when you can, or use defaults now.`;
  }

  return `That's enough for Week 1. I'll set your level to ${profile.coachLevel.toLowerCase().replace('_', ' ')}, create ${profile.teamName ?? 'your team'}, and draft three calendar sessions around ${PHASE_OF_PLAY_META[profile.focus].label.toLowerCase()}. Say yes (or click Populate calendar) to confirm.`;
}

/** Words the coach can speak (or type) to confirm a "ready to save?" prompt
 *  from Brain. Used by the Onboarding screen to auto-trigger save when the
 *  profile is already complete and the next utterance is a yes. */
const CONFIRM_PATTERN = /^(yes|yep|yeah|ok|okay|sure|go|go ahead|proceed|let'?s go|do it|sounds good|please do|save it|confirm|yes please)\.?$/i;
export function isOnboardingConfirmation(text: string): boolean {
  return CONFIRM_PATTERN.test(text.trim());
}

// ---------------------------------------------------------------------------
// MULTI-STAGE FLOW — Brain guides the coach through 7 stages, asking a
// specific question per stage. Each stage knows its required fields and
// produces a stage-specific reply when the coach answers.
// ---------------------------------------------------------------------------

export type OnboardingStage =
  | 'coach' | 'team' | 'roster' | 'season' | 'aims'
  | 'schedule' | 'plan'
  | 'philosophy' | 'curriculum'
  | 'confirm' | 'walkthrough' | 'done';

/** Full stage order. The actual route taken depends on coachLevel —
 *  `philosophy` + `curriculum` are skipped for grassroots/NOVICE coaches
 *  (Brain derives them silently). See `nextStage`. */
export const ONBOARDING_STAGE_ORDER: OnboardingStage[] = [
  'coach', 'team', 'roster', 'season', 'aims',
  'schedule', 'plan',
  'philosophy', 'curriculum',
  'confirm',
];

export interface StageMeta {
  id: OnboardingStage;
  label: string;
  description: string;
}

export const STAGE_META: Record<OnboardingStage, StageMeta> = {
  coach:       { id: 'coach',       label: 'Coach',       description: 'About you'                 },
  team:        { id: 'team',        label: 'Team',        description: 'Squad, level, league'      },
  roster:      { id: 'roster',      label: 'Roster',      description: 'Players or placeholders'   },
  season:      { id: 'season',      label: 'Season',      description: 'Where you are now'         },
  aims:        { id: 'aims',        label: 'Aims',        description: 'What to work on'           },
  schedule:    { id: 'schedule',    label: 'Schedule',    description: 'Training nights + time'    },
  plan:        { id: 'plan',        label: 'Plan',        description: 'Weeks to draft'            },
  philosophy:  { id: 'philosophy',  label: 'Philosophy',  description: 'Principles by phase'       },
  curriculum:  { id: 'curriculum',  label: 'Curriculum',  description: 'Week-by-week arc'          },
  confirm:     { id: 'confirm',     label: 'Confirm',     description: 'Lock it in'                },
  walkthrough: { id: 'walkthrough', label: 'Walkthrough', description: 'Brain reads Week 1'        },
  done:        { id: 'done',        label: 'Done',        description: 'In the app'               },
};

/** Coaches at NOVICE/GRASSROOTS get an implicit Philosophy + Curriculum
 *  (Brain derives them silently — they're referenced in the confirm prose
 *  and persist via /principles + /curriculum after save). Academy-tier
 *  coaches get explicit stages where they can shape the drafts. */
export function isImplicitPhilosophyCoach(p: OnboardingProfile): boolean {
  return p.coachLevel === 'NOVICE' || p.coachLevel === 'GRASSROOTS';
}

/** Whether a given stage has enough info to advance. The coach can always
 *  say "skip" to force-advance — `applyOnboardingInput` handles that path. */
export function isStageComplete(stage: OnboardingStage, p: OnboardingProfile): boolean {
  switch (stage) {
    case 'coach': {
      // Coach stage builds rapport — we want at least 2 signals across the
      // 7 rapport fields so Brain has tonal calibration. yearsCoaching=0
      // counts as a signal; badges='none' from a real "none" answer counts;
      // any free-text rapport field counts.
      const signals = [
        p.yearsCoaching != null,
        p.badges != null,
        p.playedTo != null,
        p.admiredCoaches != null,
        p.supportsTeam != null,
        p.enjoys != null,
        p.frustrations != null,
      ].filter(Boolean).length;
      return signals >= 2;
    }
    case 'team':
      // Required: team name, age group, squad size. Level + league + formation
      // are optional / open.
      return p.teamName !== null && p.ageGroup !== null && p.playerCount !== null;
    case 'roster':
      // null = not visited yet. [] = visited + skipped (will seed at save).
      // non-empty = coach gave names. Stage complete on either visited state.
      return p.roster !== null;
    case 'season':
      return p.seasonPhase !== null;
    case 'aims':
      // Methodology headline is the gate — Brain's prescription enriches it
      // with principles + rationale once the headline is set.
      return p.methodology !== null;
    case 'schedule':
      // Schedule stage commits to training nights + start time. Duration
      // has a sensible fallback so it doesn't block — the coach can override.
      return (p.trainingDays?.length ?? 0) > 0 && p.trainingStartTime !== null;
    case 'plan':
      return p.weeksToPlan !== null;
    case 'philosophy':
      // Academy stage — coach has seen the draft (philosophy populated)
      // and either accepted it or revised it. Stage complete once a draft
      // exists in the profile, even before they speak — Brain advances on
      // the coach's next utterance (yes / revisions).
      return p.philosophy !== null;
    case 'curriculum':
      return p.curriculum !== null;
    case 'confirm':
    case 'walkthrough':
    case 'done':
      return true;
  }
}

export function nextStage(stage: OnboardingStage, p?: OnboardingProfile): OnboardingStage {
  const i = ONBOARDING_STAGE_ORDER.indexOf(stage);
  if (i < 0 || i === ONBOARDING_STAGE_ORDER.length - 1) return 'done';
  let next = ONBOARDING_STAGE_ORDER[i + 1];
  // Skip the philosophy + curriculum stages for grassroots/NOVICE — Brain
  // derives them silently and the confirm prose mentions them.
  if (p && isImplicitPhilosophyCoach(p)) {
    while (next === 'philosophy' || next === 'curriculum') {
      const j = ONBOARDING_STAGE_ORDER.indexOf(next);
      if (j === ONBOARDING_STAGE_ORDER.length - 1) return 'done';
      next = ONBOARDING_STAGE_ORDER[j + 1];
    }
  }
  return next;
}

export function prevStage(stage: OnboardingStage, p?: OnboardingProfile): OnboardingStage {
  const i = ONBOARDING_STAGE_ORDER.indexOf(stage);
  if (i <= 0) return 'coach';
  let prev = ONBOARDING_STAGE_ORDER[i - 1];
  if (p && isImplicitPhilosophyCoach(p)) {
    while ((prev === 'philosophy' || prev === 'curriculum')) {
      const j = ONBOARDING_STAGE_ORDER.indexOf(prev);
      if (j === 0) return 'coach';
      prev = ONBOARDING_STAGE_ORDER[j - 1];
    }
  }
  return prev;
}

/** The question Brain asks at the start of each stage. Personalised with the
 *  coach's name when we have one (from auth). */
export function stageQuestion(stage: OnboardingStage, p: OnboardingProfile, coachFirstName?: string | null): string {
  const hi = coachFirstName ? `Hi ${coachFirstName}, ` : '';
  switch (stage) {
    case 'coach':
      return `${hi}good to meet you. I'm Brain — I'll walk you through a quick setup (a bit about you, your team, what you want to work on this block) and then draft your first weeks of sessions onto the calendar. The questions are just so I can pitch this right for you, so take it at your own pace and skip anything you'd rather not get into. To kick off — how long have you been coaching?`;
    case 'team':
      return `Tell me about the team — name, age group, squad size, what level you're at (grassroots, competitive, academy), and the league if you're in one. Throw in a formation if you've got one in mind.`;
    case 'roster':
      return `Want to give me the roster while we're here — names, even rough positions if you know them — or say "skip" and I'll seed ${p.playerCount ?? 12} placeholders you can swap out later?`;
    case 'season':
      return `Where are you in the season right now? Pre-season, league running, playoffs? And mention anything specific on the horizon — a tournament, league restart, opening fixture.`;
    case 'aims':
      return `What's the one thing you want them to get better at this block? Could be defending shape, building from the back, decision-making under pressure — whatever's at the top of your mind. I'll shape it into a methodology you can actually train.`;
    case 'schedule':
      return `When are you on the grass? Most U11 grassroots sides train twice a week — Tuesday and Thursday, 60 minutes, 6:30pm. Does that match yours, or are you running different nights?`;
    case 'plan':
      return `How many weeks should this block run? Four to six is the range — Ben Petty's rule is anything less and you can't see if learning is sticking; anything more and they get bored. Four's the usual starting point.`;
    case 'philosophy':
      // Brain shows the derived draft and asks the coach to react. The
      // draft itself is rendered in the right-side stage card; this
      // sentence just opens the conversation.
      return p.philosophy
        ? `Here's a starting philosophy I've drawn up from what you've told me — four principles per phase. Have a look on the right and tell me what needs adjusting, or say "looks right" and we'll move on.`
        : `Right — let me draft a philosophy from what you've told me. One sec.`;
    case 'curriculum':
      return p.curriculum
        ? `And here's the ${p.curriculum.mesocycle.weekCount}-week mesocycle — one subject (${p.curriculum.mesocycle.headline}) across the whole block, with the variety coming from week-by-week sub-themes. That's deliberate — flipping subjects week to week makes it impossible to see whether anything's landing. Want to tweak a sub-theme, or are you happy with that arc?`
        : `Building the mesocycle now — one subject across the block.`;
    case 'confirm': {
      const meso = p.curriculum?.mesocycle;
      const lines = [
        p.teamName && p.ageGroup
          ? `${p.teamName} — ${p.ageGroup}, ${p.playerCount ?? '?'} players, ${(p.coachLevel ?? '').toLowerCase().replace('_', ' ') || 'grassroots'}`
          : null,
        p.leagueName ? `league: ${p.leagueName}` : null,
        p.formation ? `formation: ${p.formation}` : null,
        p.roster && p.roster.length > 0
          ? `roster: ${p.roster.length} ${p.roster[0]?.placeholder ? 'placeholders' : 'players'}`
          : null,
        p.seasonPhase ? `season: ${p.seasonPhase.replace('_', ' ')}` : null,
        p.methodology ? `aim: ${p.methodology}` : null,
        p.methodologyPrinciples && p.methodologyPrinciples.length > 0
          ? `principles: ${p.methodologyPrinciples.slice(0, 3).join(' · ')}`
          : null,
        p.trainingDays && p.trainingDays.length > 0
          ? `schedule: ${p.trainingDays.join('/')}${p.trainingStartTime ? ` at ${p.trainingStartTime}` : ''}, ${p.sessionDurationMins}-min sessions`
          : null,
        // For grassroots, mention the derived philosophy/curriculum in
        // plain prose — they didn't see the stages but they should know
        // Brain has built a premise for the sessions.
        p.philosophy && isImplicitPhilosophyCoach(p)
          ? `philosophy: ${p.philosophy.defend[0]?.text ?? 'staying organised'} · ${p.philosophy.attack[0]?.text ?? 'moving the ball forward'}`
          : null,
        meso && isImplicitPhilosophyCoach(p)
          ? `${meso.weekCount}-week ${meso.phaseEmphasis} mesocycle on "${meso.headline}": ${meso.weeks.slice(0, 3).map((w) => w.subTheme.toLowerCase()).join(' → ')}${meso.weeks.length > 3 ? ' → …' : ''}`
          : null,
      ].filter(Boolean);
      return `Right — here's what I've got:\n• ${lines.join('\n• ')}\n\nSay yes and I'll save it. Or tell me what to change.`;
    }
    case 'walkthrough':
      return `Calendar's drafted. Want me to walk you through Week 1 session-by-session, or would you rather open the calendar and have a look yourself?`;
    case 'done':
      return `Done. You're in the app now.`;
  }
}

/** Brain's confirmation-style reply after the coach answers a stage. Keeps
 *  the conversation flowing naturally rather than echoing raw extraction. */
export function stageAcknowledgement(stage: OnboardingStage, p: OnboardingProfile, advanced: boolean): string {
  if (!advanced) {
    // Stage wasn't filled — gentle nudge.
    switch (stage) {
      case 'coach':       return `No worries, say "skip" if you'd rather not share. Or tell me your coaching years or any badge you've got.`;
      case 'team':        return `Need a bit more — team name, age group, squad size. Drop them in any order.`;
      case 'roster':      return `Say "skip" and I'll seed placeholder players, or tell me a few names.`;
      case 'season':      return `Tell me where you are — pre-season, mid-season, playoffs, or just the league phase.`;
      case 'aims':        return `What's the headline aim? Defending, possession, decision-making — one or two themes.`;
      case 'schedule':    return `Which nights and what time? Tue/Thu 6:30pm is the usual — yours different?`;
      case 'plan':        return `Give me a number of weeks — anything from one to twelve.`;
      case 'confirm':     return `Say "yes" to save, or tell me what to change.`;
      case 'walkthrough': return `Say "walk me through it" or tap Open Calendar.`;
      case 'done':        return '';
      case 'philosophy':
      case 'curriculum':  return '';
    }
  }
  const next = nextStage(stage);
  // The next stage's question chains in naturally.
  const opener = (() => {
    switch (stage) {
      case 'coach':  return p.yearsCoaching ? `Got it, ${p.yearsCoaching} year${p.yearsCoaching === 1 ? '' : 's'} in.` : `Noted.`;
      case 'team':   return `Got ${p.teamName ?? 'the team'}.`;
      case 'roster': return p.roster && p.roster.length > 0 && p.roster[0].placeholder
        ? `Seeded ${p.roster.length} placeholder players — you can edit them later.`
        : `Roster locked in.`;
      case 'season': return `Season noted.`;
      case 'aims':   return p.methodology
        ? `Right — "${p.methodology}" it is.`
        : `Got it — we'll bias sessions toward that.`;
      case 'schedule': {
        const days = p.trainingDays?.join('/') ?? 'Tue/Thu';
        return `${days} ${p.trainingStartTime ?? '18:30'}, ${p.sessionDurationMins ?? 60}-min sessions — locked.`;
      }
      case 'plan':   return `${p.weeksToPlan} weeks it is.`;
      case 'philosophy': return `Cheers — moving on.`;
      case 'curriculum': return `Right, moving on.`;
      case 'confirm': return ``;
      case 'walkthrough': return ``;
      case 'done':   return ``;
    }
  })();
  if (next === 'done') return opener;
  return `${opener} ${stageQuestion(next, p)}`.trim();
}

// ---------------------------------------------------------------------------
// FUZZY EDIT INTENT — detect when the coach wants to change a previously
// captured field. Returns the target stage so the UI can flash + scroll.
// ---------------------------------------------------------------------------

export interface EditIntent {
  /** The stage whose field is being edited. */
  stage: OnboardingStage;
  /** The single field most likely targeted (best effort). */
  field?: keyof OnboardingProfile;
}

const EDIT_VERBS = '(?:change|update|set|fix|edit|actually|make it|correct|switch)';
const FIELD_PATTERNS: Array<{ re: RegExp; stage: OnboardingStage; field?: keyof OnboardingProfile }> = [
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\b(team name|the name)\\b`, 'i'), stage: 'team', field: 'teamName' },
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\bage(?:\\s+group)?\\b`, 'i'),    stage: 'team', field: 'ageGroup' },
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\b(?:squad|players?|roster size)\\b`, 'i'), stage: 'team', field: 'playerCount' },
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\b(level|tier|grassroots|academy|competitive)\\b`, 'i'), stage: 'team', field: 'coachLevel' },
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\bleague\\b`, 'i'),                stage: 'team', field: 'leagueName' },
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\bformation\\b`, 'i'),             stage: 'team', field: 'formation' },
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\b(badge|qualification|years? coaching)\\b`, 'i'), stage: 'coach', field: 'badges' },
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\b(roster|players? list|line ?up)\\b`, 'i'), stage: 'roster', field: 'roster' },
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\b(season|tournament|fixture)\\b`, 'i'), stage: 'season', field: 'seasonPhase' },
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\b(focus|aim|methodology|theme|priority)\\b`, 'i'), stage: 'aims', field: 'methodology' },
  { re: new RegExp(`\\b${EDIT_VERBS}\\b.*\\b(weeks|plan length|horizon)\\b`, 'i'), stage: 'plan', field: 'weeksToPlan' },
];

/** Detect whether the coach is trying to revise an earlier field. Returns
 *  null when the utterance looks like a normal stage answer. */
export function detectEditIntent(text: string): EditIntent | null {
  const t = text.trim();
  if (/^go back\b|^back up\b|^previous\b/i.test(t)) {
    // generic backstep — caller decides which stage. Surfaced as 'coach' so
    // the caller can prevStage from current.
    return { stage: 'coach' };
  }
  for (const { re, stage, field } of FIELD_PATTERNS) {
    if (re.test(t)) return { stage, field };
  }
  return null;
}

/** "skip" / "skip this" / "you decide" — punt the current stage. Field
 *  becomes null/empty, stage advances. */
const SKIP_PATTERN = /^(?:skip(?:\s+(?:this|that|it))?|you decide|don'?t (?:know|care)|n\/?a|no thanks|nothing|none)\.?$/i;
export function isSkipRequest(text: string): boolean {
  return SKIP_PATTERN.test(text.trim());
}

/** Fill only the fields that belong to a given stage with sensible defaults.
 *  Called when the coach says "skip" — the stage advances without erasing
 *  anything the coach has provided. */
export function fillStageDefaults(stage: OnboardingStage, p: OnboardingProfile): OnboardingProfile {
  switch (stage) {
    case 'coach':
      return {
        ...p,
        yearsCoaching: p.yearsCoaching ?? 1,
        badges: p.badges ?? 'none',
        // Skip on coach stage = "let's just move on" — stamp the rapport
        // fields as 'skipped' so isStageComplete's 2-signal threshold
        // satisfies even without genuine answers. Brain still has years
        // + badges + a marker; it just won't have tonal calibration.
        playedTo: p.playedTo ?? 'skipped',
        admiredCoaches: p.admiredCoaches,
        supportsTeam: p.supportsTeam,
        enjoys: p.enjoys,
        frustrations: p.frustrations,
      };
    case 'team': {
      const ageGroup = p.ageGroup ?? 'U12';
      return {
        ...p,
        ageGroup,
        teamName: p.teamName ?? `Aurora FC ${ageGroup}`,
        playerCount: p.playerCount ?? 12,
      };
    }
    case 'roster':
      // Seed real placeholder rows immediately so the right panel shows them
      // (rather than holding off until save). `placeholder: true` flags them
      // so downstream UI can render them visually distinct.
      return {
        ...p,
        roster: p.roster ?? seedPlaceholderRoster(p.playerCount ?? 12),
      };
    case 'season':
      return { ...p, seasonPhase: p.seasonPhase ?? 'pre_season' };
    case 'aims':
      return { ...p, methodology: p.methodology ?? 'FA 4-Corner Model' };
    case 'schedule': {
      const days = p.trainingDays && p.trainingDays.length > 0 ? p.trainingDays : ['Tue', 'Thu'];
      return {
        ...p,
        trainingDays: days,
        trainingStartTime: p.trainingStartTime ?? '18:30',
        trainingDaysPerWeek: days.length,
        sessionDurationMins: p.sessionDurationMins ?? 60,
      };
    }
    case 'plan':
      return { ...p, weeksToPlan: p.weeksToPlan ?? 4 };
    case 'philosophy':
      // Skip = accept Brain's draft as-is. The draft must already exist
      // (derived as a side-effect when the stage becomes active). If for
      // some reason it doesn't, advance without one — Brain will draft
      // later inline.
      return p;
    case 'curriculum':
      return p;
    case 'confirm':
    case 'walkthrough':
    case 'done':
      return p;
  }
}

/** Count of how many stages (excluding 'done') are complete. Used by the
 *  progress count on the right-side artefact panel. */
export function completedStageCount(p: OnboardingProfile): number {
  return ONBOARDING_STAGE_ORDER.filter((s) => isStageComplete(s, p)).length;
}

function nextWeekday(from: Date, weekday: number, minOffsetDays: number): Date {
  const out = new Date(from);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() + minOffsetDays);
  const delta = (weekday - out.getDay() + 7) % 7;
  out.setDate(out.getDate() + delta);
  return out;
}

function dateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildPhaseCoverage(focus: PhaseOfPlay, shift: number): PhaseCoverage {
  const base = PHASE_COVERAGE_BY_FOCUS[focus];
  const secondary = PHASES_OF_PLAY[(PHASES_OF_PLAY.indexOf(focus) + shift) % PHASES_OF_PLAY.length];
  return normalisePhaseCoverage({
    ...base,
    [secondary]: base[secondary] + 10,
    [focus]: Math.max(35, base[focus] - 10),
  });
}

export function buildOnboardingSessions(profile: OnboardingProfile, now = new Date()): OnboardingSessionPlan[] {
  const age = profile.ageGroup ?? 'U12';
  const phaseLabel = PHASE_OF_PLAY_META[profile.focus].label;
  const methodology = profile.methodology ?? 'your team principles';
  const dates = [
    nextWeekday(now, 2, 1),
    nextWeekday(now, 4, 2),
    nextWeekday(now, 6, 3),
  ];

  return [
    {
      date: dateOnly(dates[0]),
      title: `${age} ${phaseLabel} primer`,
      objectives: [`Introduce the ${phaseLabel.toLowerCase()} theme`, 'Keep the first coaching point simple'],
      phaseCoverage: buildPhaseCoverage(profile.focus, 0),
      psychSocialIntent: `Build confidence quickly; connect the work to ${methodology}.`,
    },
    {
      date: dateOnly(dates[1]),
      title: `${age} decision rehearsal`,
      objectives: ['Repeat the trigger', 'Add one decision under pressure'],
      phaseCoverage: buildPhaseCoverage(profile.focus, 1),
      psychSocialIntent: 'Players should leave knowing the cue, not just the drill.',
    },
    {
      date: dateOnly(dates[2]),
      title: `${age} match-day tune-up`,
      objectives: ['Rehearse the match cue', 'Finish with a constrained game'],
      phaseCoverage: buildPhaseCoverage(profile.focus, 2),
      psychSocialIntent: 'Short, upbeat, and easy to carry onto the touchline.',
    },
  ];
}

function profileForSave(profile: OnboardingProfile): OnboardingProfile {
  return {
    ...profile,
    teamName: profile.teamName ?? `Aurora FC ${profile.ageGroup ?? 'U12'}`,
    ageGroup: profile.ageGroup ?? 'U12',
    playerCount: profile.playerCount ?? 12,
  };
}

async function ensureCoachOrg(coachId: string, profile: OnboardingProfile): Promise<string> {
  const { data: userData, error: userError } = await supabase
    .from('users')
    .select('id, org_id, email, name')
    .eq('id', coachId)
    .maybeSingle();
  if (userError) throw new Error(
    userError.message?.toLowerCase().includes('failed to fetch')
      ? 'Could not reach Supabase — check VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY in apps/web2/.env.local.'
      : `Could not load coach user: ${userError.message}`
  );

  const user = userData as UserRow | null;
  if (user?.org_id) return user.org_id;

  const orgName = profile.teamName?.replace(/\s+U\d{1,2}$/i, '') || 'Independent Coach';
  const { data: orgData, error: orgError } = await supabase
    .from('organizations')
    .insert({ name: orgName, country: 'GB', timezone: 'Europe/London' })
    .select('id')
    .single();
  if (orgError || !orgData) throw new Error(`Could not create organization: ${orgError?.message ?? 'no row returned'}`);

  const orgId = (orgData as IdRow).id;
  const { error: updateError } = await supabase
    .from('users')
    .update({ org_id: orgId })
    .eq('id', coachId);
  if (updateError) throw new Error(`Could not attach coach to organization: ${updateError.message}`);
  return orgId;
}

async function upsertTeam(coachId: string, orgId: string, profile: OnboardingProfile): Promise<string> {
  const teamPayload = {
    org_id: orgId,
    coach_id: coachId,
    name: profile.teamName ?? `Aurora FC ${profile.ageGroup ?? 'U12'}`,
    age_group: profile.ageGroup ?? 'U12',
    player_count: profile.playerCount ?? 12,
    formation_default: profile.formation,
    proficiency_level: profile.coachLevel === 'NOVICE' ? 'FOUNDATION' : profile.coachLevel,
    training_days_per_week: profile.trainingDaysPerWeek,
    session_duration_mins: profile.sessionDurationMins,
    season_start: dateOnly(new Date()),
  };

  const { data: existingData, error: existingError } = await supabase
    .from('teams')
    .select('id')
    .eq('coach_id', coachId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (existingError) throw new Error(`Could not check existing team: ${existingError.message}`);

  const existing = existingData as IdRow | null;
  if (existing) {
    const { error: updateError } = await supabase
      .from('teams')
      .update(teamPayload)
      .eq('id', existing.id);
    if (updateError) throw new Error(`Could not update team: ${updateError.message}`);
    return existing.id;
  }

  const { data, error } = await supabase
    .from('teams')
    .insert(teamPayload)
    .select('id')
    .single();
  if (error || !data) throw new Error(`Could not create team: ${error?.message ?? 'no row returned'}`);
  return (data as IdRow).id;
}

async function saveCoachProfile(coachId: string, profile: OnboardingProfile): Promise<string | null> {
  const commsStyle: CommsStyle = defaultCommsFor(profile.coachLevel);
  const profilePct = computeCoachProfilePct({
    coach_level: profile.coachLevel,
    years_coaching: profile.yearsCoaching,
    age_groups_coached: profile.ageGroup ? [profile.ageGroup] : [],
    coach_badges: [],
    coach_methodology: profile.methodology,
    coach_comms_style: commsStyle,
  });

  // Coach profile columns live on users table directly per migration 00025
  // — no separate coach_profiles table. ensureCoachUser ran at auth, so the
  // row exists and update is fine.
  const { error } = await supabase
    .from('users')
    .update({
      coach_level: profile.coachLevel,
      years_coaching: profile.yearsCoaching,
      age_groups_coached: profile.ageGroup ? [profile.ageGroup] : [],
      coach_methodology: profile.methodology,
      coach_comms_style: commsStyle,
      coach_profile_extra: {
        onboarding_focus: profile.focus,
        onboarding_focus_rationale: profile.focusRationale,
        onboarding_methodology_principles: profile.methodologyPrinciples,
        onboarding_methodology_rationale: profile.methodologyRationale,
        onboarding_training_days: profile.trainingDays,
        onboarding_training_start_time: profile.trainingStartTime,
        formation: profile.formation,
        player_count: profile.playerCount,
      },
      coach_profile_pct: profilePct,
    })
    .eq('id', coachId);

  return error ? error.message : null;
}

async function saveCurriculumPlan(teamId: string, coachId: string, profile: OnboardingProfile, sessions: OnboardingSessionPlan[]): Promise<string | null> {
  const seasonStart = sessions[0]?.date ?? dateOnly(new Date());
  const seasonEndDate = new Date(`${seasonStart}T00:00:00`);
  seasonEndDate.setDate(seasonEndDate.getDate() + 84);

  const { error } = await supabase
    .from('curriculum_plans')
    .insert({
      team_id: teamId,
      coach_id: coachId,
      season_start: seasonStart,
      season_end: dateOnly(seasonEndDate),
      plan_data: {
        source: 'web2_onboarding',
        primary_phase: profile.focus,
        methodology: profile.methodology,
        week_1: sessions,
      },
      is_active: true,
    });

  return error ? error.message : null;
}

async function upsertSessions(teamId: string, coachId: string, profile: OnboardingProfile, sessions: OnboardingSessionPlan[]): Promise<number> {
  let count = 0;
  for (let i = 0; i < sessions.length; i += 1) {
    const session = sessions[i];
    const payload = {
      team_id: teamId,
      coach_id: coachId,
      date: session.date,
      title: session.title,
      objectives: session.objectives,
      corner_coverage: CORNER_COVERAGE,
      phase_coverage: session.phaseCoverage,
      psych_social_intent: session.psychSocialIntent,
      duration_mins: profile.sessionDurationMins,
      curriculum_week: 1,
      status: 'DRAFT',
    };

    const { data: existingData, error: existingError } = await supabase
      .from('sessions')
      .select('id')
      .eq('team_id', teamId)
      .eq('date', session.date)
      .limit(1)
      .maybeSingle();
    if (existingError) throw new Error(`Could not check session for ${session.date}: ${existingError.message}`);

    const existing = existingData as IdRow | null;
    const result = existing
      ? await supabase.from('sessions').update(payload).eq('id', existing.id)
      : await supabase.from('sessions').insert(payload);
    if (result.error) throw new Error(`Could not save session for ${session.date}: ${result.error.message}`);
    count += 1;
  }
  return count;
}

export async function saveOnboardingPlan(coachId: string, rawProfile: OnboardingProfile): Promise<SaveOnboardingResult> {
  const profile = profileForSave(rawProfile);
  const warnings: string[] = [];
  const sessions = buildOnboardingSessions(profile);
  const orgId = await ensureCoachOrg(coachId, profile);
  const teamId = await upsertTeam(coachId, orgId, profile);

  const profileWarning = await saveCoachProfile(coachId, profile);
  if (profileWarning) warnings.push(`Coach profile was not saved: ${profileWarning}`);

  const curriculumWarning = await saveCurriculumPlan(teamId, coachId, profile, sessions);
  if (curriculumWarning) warnings.push(`Curriculum plan was not saved: ${curriculumWarning}`);

  const sessionCount = await upsertSessions(teamId, coachId, profile, sessions);
  return { teamId, sessionCount, warnings };
}
